export type SupportedAiLocale =
  | "en"
  | "es"
  | "fr"
  | "de"
  | "it"
  | "pt"
  | "zh"
  | "ja"
  | "ko"
  | "ru"
  | "ar"
  | "hi";

type MessageKey =
  | "answer_document_title"
  | "processing_in_background"
  | "generic_processing_error"
  | "answer_timed_out_error"
  | "answer_service_unavailable_error"
  | "answer_failed_after_actions_error"
  | "answer_withheld_error"
  | "task_failed_error"
  | "task_view_failed_error"
  | "task_timed_out_error"
  | "task_cancelled_error"
  | "named_task_line"
  | "async_results_ready"
  | "opened_requested_component"
  | "closed_requested_component"
  | "view_already_clear"
  | "protected_component_cannot_close"
  | "enable_location_access"
  | "capability_offer_to_team"
  | "need_component_to_close"
  | "need_ui_component_request"
  | "ambiguous_component_edit"
  | "componentbuilder_edit_blocked"
  | "edit_request_rephrase"
  | "component_undo_ready"
  | "component_undo_changed"
  | "component_undo_done"
  | "component_undo_already_done"
  | "component_undo_unavailable"
  | "component_updated"
  | "component_edit_error"
  | "need_communication_action"
  | "confirm_new_recipient"
  | "workflow_recipient_not_approved"
  | "confirm_message_text"
  | "notifications_error"
  | "no_new_notifications_today"
  | "notifications_digest_one"
  | "notifications_digest_many"
  | "notifications_digest_action_one"
  | "notifications_digest_action_many"
  | "feed_item_email"
  | "feed_item_message"
  | "feed_item_messages"
  | "feed_item_message_on"
  | "feed_item_messages_on"
  | "feed_item_mention_on"
  | "feed_item_calendar"
  | "feed_item_contact"
  | "feed_item_attachment"
  | "feed_item_scout"
  | "feed_item_update"
  | "browser_service_not_configured"
  | "browser_task_required"
  | "browser_task_started"
  | "scan_opened"
  | "link_check_ask"
  | "link_check_failed"
  | "link_check_not_web"
  | "link_check_runs_code"
  | "link_check_reported"
  | "link_check_listed"
  | "link_check_danger"
  | "link_check_hidden"
  | "link_check_caution"
  | "link_check_incomplete"
  | "link_check_no_known"
  | "link_check_commons_clear"
  | "link_check_commons_unavailable"
  | "link_check_commons_not_shared"
  | "link_check_commons_not_applicable"
  | "browser_needs_input"
  | "browser_input_prompt"
  | "browser_failed_with_detail"
  | "assisted_browse_started"
  | "assisted_browse_needs_go_ahead"
  | "assisted_browse_confirm_on_card"
  | "assisted_browse_site_required"
  | "browser_task_success"
  | "browser_generic_error"
  | "document_create_error"
  | "excel_data_structure_error"
  | "excel_config_error"
  | "excel_created"
  | "document_plan_created"
  | "cannot_edit_email_here"
  | "need_document_filename"
  | "document_edit_target_ambiguous"
  | "document_edit_identity_mismatch"
  | "document_edit_source_incomplete"
  | "document_edit_conflict"
  | "file_type_not_editable"
  | "slide_content_not_loaded"
  | "document_content_not_loaded"
  | "no_editable_pages_updated"
  | "document_updated_current_viewer"
  | "document_edited_displayed"
  | "document_edit_error"
  | "convert_need_filename"
  | "convert_need_output_format"
  | "convert_already_format"
  | "convert_success"
  | "convert_unsupported"
  | "convert_error"
  | "need_more_source_information"
  | "document_operation_prompt_required"
  | "search_results_fallback"
  | "search_title"
  | "search_completed_reason"
  | "google_search_error"
  | "generated_image_title"
  | "generated_image_description"
  | "generated_images_reason"
  | "generated_images_error";

type MessageVars = Record<string, string | number | null | undefined>;

const SUPPORTED_LOCALES = new Set<SupportedAiLocale>([
  "en",
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "zh",
  "ja",
  "ko",
  "ru",
  "ar",
  "hi",
]);

function interpolate(template: string, vars: MessageVars = {}): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""));
}

export function normalizeAiOutputLanguage(language?: string | null): SupportedAiLocale {
  const normalized = String(language || "en")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0] as SupportedAiLocale;

  return SUPPORTED_LOCALES.has(normalized) ? normalized : "en";
}

const AI_OUTPUT_LANGUAGE_NAMES = new Intl.DisplayNames(["en"], { type: "language" });

/** English name of the output language, for model instructions: "es" gives "Spanish". */
export function aiOutputLanguageName(language?: string | null): string {
  const locale = normalizeAiOutputLanguage(language);
  return AI_OUTPUT_LANGUAGE_NAMES.of(locale) ?? locale;
}

const MESSAGES: Record<MessageKey, Record<SupportedAiLocale, string>> = {
  answer_document_title: {
    en: "Details",
    es: "Detalles",
    fr: "Détails",
    de: "Details",
    it: "Dettagli",
    pt: "Detalhes",
    zh: "详情",
    ja: "詳細",
    ko: "세부 정보",
    ru: "Подробности",
    ar: "التفاصيل",
    hi: "विवरण",
  },
  processing_in_background: {
    en: "I'm processing your request in the background.",
    es: "Estoy procesando tu solicitud en segundo plano.",
    fr: "Je traite votre demande en arrière-plan.",
    de: "Ich bearbeite Ihre Anfrage im Hintergrund.",
    it: "Sto elaborando la tua richiesta in background.",
    pt: "Estou processando sua solicitação em segundo plano.",
    zh: "我正在后台处理你的请求。",
    ja: "リクエストをバックグラウンドで処理しています。",
    ko: "요청을 백그라운드에서 처리하고 있습니다.",
    ru: "Я обрабатываю ваш запрос в фоновом режиме.",
    ar: "أعالج طلبك في الخلفية.",
    hi: "मैं आपके अनुरोध को बैकग्राउंड में प्रोसेस कर रहा हूँ।",
  },
  generic_processing_error: {
    en: "I'm sorry, an error occurred while processing the response. Please try again.",
    es: "Lo siento, ocurrió un error al procesar la respuesta. Inténtalo de nuevo.",
    fr: "Je suis désolé, une erreur s'est produite lors du traitement de la réponse. Veuillez réessayer.",
    de: "Es tut mir leid, beim Verarbeiten der Antwort ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Mi dispiace, si è verificato un errore durante l'elaborazione della risposta. Riprova.",
    pt: "Desculpe, ocorreu um erro ao processar a resposta. Tente novamente.",
    zh: "抱歉，处理响应时出错了。请再试一次。",
    ja: "申し訳ありません。応答の処理中にエラーが発生しました。もう一度お試しください。",
    ko: "죄송합니다. 응답을 처리하는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "Извините, при обработке ответа произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "عذرًا، حدث خطأ أثناء معالجة الرد. يرجى المحاولة مرة أخرى.",
    hi: "माफ़ कीजिए, उत्तर प्रोसेस करते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  answer_timed_out_error: {
    en: "A service I need took too long, so I stopped this answer. Please try again.",
    es: "Un servicio que necesito tardó demasiado, así que detuve esta respuesta. Inténtalo de nuevo.",
    fr: "Un service dont j'ai besoin a mis trop de temps, j'ai donc arrêté cette réponse. Veuillez réessayer.",
    de: "Ein Dienst, den ich brauche, hat zu lange gebraucht, deshalb habe ich diese Antwort abgebrochen. Bitte versuchen Sie es erneut.",
    it: "Un servizio di cui ho bisogno ha impiegato troppo tempo, quindi ho interrotto questa risposta. Riprova.",
    pt: "Um serviço de que preciso demorou demais, então interrompi esta resposta. Tente novamente.",
    zh: "我需要的一项服务响应太慢，所以我停止了这次回答。请再试一次。",
    ja: "必要なサービスの応答に時間がかかりすぎたため、この回答を中止しました。もう一度お試しください。",
    ko: "필요한 서비스의 응답이 너무 오래 걸려 이 답변을 중단했습니다. 다시 시도해 주세요.",
    ru: "Нужный мне сервис отвечал слишком долго, поэтому я остановил этот ответ. Пожалуйста, попробуйте еще раз.",
    ar: "استغرقت خدمة أحتاجها وقتًا طويلًا، لذلك أوقفت هذا الرد. يرجى المحاولة مرة أخرى.",
    hi: "जिस सेवा की मुझे ज़रूरत है उसने बहुत देर लगाई, इसलिए मैंने यह उत्तर रोक दिया। कृपया फिर से कोशिश करें।",
  },
  answer_service_unavailable_error: {
    en: "A service I need is busy or unreachable right now. Please try again in a moment.",
    es: "Un servicio que necesito está ocupado o no responde ahora mismo. Inténtalo de nuevo en un momento.",
    fr: "Un service dont j'ai besoin est occupé ou injoignable pour le moment. Veuillez réessayer dans un instant.",
    de: "Ein Dienst, den ich brauche, ist gerade ausgelastet oder nicht erreichbar. Bitte versuchen Sie es gleich noch einmal.",
    it: "Un servizio di cui ho bisogno è occupato o non raggiungibile in questo momento. Riprova tra un momento.",
    pt: "Um serviço de que preciso está ocupado ou inacessível agora. Tente novamente em instantes.",
    zh: "我需要的一项服务目前繁忙或无法访问。请稍后再试。",
    ja: "必要なサービスが現在混み合っているか、接続できません。少ししてからもう一度お試しください。",
    ko: "필요한 서비스가 지금 사용 중이거나 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.",
    ru: "Нужный мне сервис сейчас перегружен или недоступен. Пожалуйста, попробуйте еще раз через минуту.",
    ar: "خدمة أحتاجها مشغولة أو يتعذّر الوصول إليها الآن. يرجى المحاولة مرة أخرى بعد قليل.",
    hi: "जिस सेवा की मुझे ज़रूरत है वह अभी व्यस्त है या उस तक पहुँचा नहीं जा सकता। कृपया थोड़ी देर बाद फिर से कोशिश करें।",
  },
  answer_failed_after_actions_error: {
    en: "Your request may already have run, but I couldn't finish the answer. Check the result before you try again, so nothing runs twice.",
    es: "Es posible que tu solicitud ya se haya ejecutado, pero no pude terminar la respuesta. Revisa el resultado antes de volver a intentarlo para que nada se haga dos veces.",
    fr: "Votre demande a peut-être déjà été exécutée, mais je n'ai pas pu terminer la réponse. Vérifiez le résultat avant de réessayer, pour que rien ne soit fait deux fois.",
    de: "Ihre Anfrage wurde möglicherweise schon ausgeführt, aber ich konnte die Antwort nicht fertigstellen. Prüfen Sie das Ergebnis, bevor Sie es erneut versuchen, damit nichts doppelt ausgeführt wird.",
    it: "La tua richiesta potrebbe essere già stata eseguita, ma non sono riuscito a completare la risposta. Controlla il risultato prima di riprovare, così nulla viene eseguito due volte.",
    pt: "Sua solicitação pode já ter sido executada, mas não consegui concluir a resposta. Confira o resultado antes de tentar novamente, para que nada seja feito duas vezes.",
    zh: "你的请求可能已经执行，但我未能完成回答。再试之前请先检查结果，以免重复执行。",
    ja: "リクエストはすでに実行された可能性がありますが、回答を完成できませんでした。二重に実行されないよう、もう一度試す前に結果を確認してください。",
    ko: "요청이 이미 실행되었을 수 있지만 답변을 마치지 못했습니다. 중복 실행되지 않도록 다시 시도하기 전에 결과를 확인해 주세요.",
    ru: "Возможно, ваш запрос уже выполнен, но я не смог закончить ответ. Проверьте результат, прежде чем пробовать снова, чтобы ничего не выполнилось дважды.",
    ar: "ربما نُفِّذ طلبك بالفعل، لكن لم أتمكن من إكمال الرد. تحقّق من النتيجة قبل المحاولة مرة أخرى حتى لا يُنفَّذ شيء مرتين.",
    hi: "हो सकता है आपका अनुरोध पहले ही चल चुका हो, लेकिन मैं उत्तर पूरा नहीं कर सका। फिर से कोशिश करने से पहले नतीजा देख लें, ताकि कुछ भी दो बार न चले।",
  },
  answer_withheld_error: {
    en: "Who can see this answer changed while I was writing it, so I held it back. Ask again for an up-to-date answer. If you asked me to change something, check it first.",
    es: "Quién puede ver esta respuesta cambió mientras la escribía, así que la retuve. Vuelve a preguntar para obtener una respuesta actualizada. Si me pediste cambiar algo, revísalo primero.",
    fr: "Les personnes qui peuvent voir cette réponse ont changé pendant que je l'écrivais, je l'ai donc retenue. Reposez la question pour obtenir une réponse à jour. Si vous m'avez demandé de modifier quelque chose, vérifiez-le d'abord.",
    de: "Wer diese Antwort sehen darf, hat sich geändert, während ich sie geschrieben habe. Deshalb habe ich sie zurückgehalten. Fragen Sie erneut, um eine aktuelle Antwort zu erhalten. Wenn Sie mich gebeten haben, etwas zu ändern, prüfen Sie das zuerst.",
    it: "Chi può vedere questa risposta è cambiato mentre la scrivevo, quindi l'ho trattenuta. Chiedi di nuovo per avere una risposta aggiornata. Se mi hai chiesto di cambiare qualcosa, controllalo prima.",
    pt: "Quem pode ver esta resposta mudou enquanto eu a escrevia, então eu a retive. Pergunte de novo para obter uma resposta atualizada. Se você me pediu para mudar algo, confira primeiro.",
    zh: "在我撰写这条回答时，可以查看它的人发生了变化，所以我暂不显示。请再问一次以获取最新回答。如果你让我更改了什么，请先检查一下。",
    ja: "回答の作成中に、この回答を見られる人が変わったため、表示を控えました。最新の回答を得るには、もう一度質問してください。何かの変更を依頼した場合は、先にそれを確認してください。",
    ko: "답변을 작성하는 동안 이 답변을 볼 수 있는 사람이 바뀌어 표시하지 않았습니다. 최신 답변을 받으려면 다시 질문해 주세요. 무언가 변경을 요청하셨다면 먼저 확인해 주세요.",
    ru: "Пока я писал этот ответ, изменилось, кто может его видеть, поэтому я его придержал. Спросите еще раз, чтобы получить актуальный ответ. Если вы просили что-то изменить, сначала проверьте это.",
    ar: "تغيّر من يمكنه رؤية هذا الرد أثناء كتابته، لذلك حجبته. اسأل مرة أخرى للحصول على رد محدَّث. إذا طلبت مني تغيير شيء، فتحقّق منه أولًا.",
    hi: "जब मैं यह उत्तर लिख रहा था, तब इसे देख सकने वाले लोग बदल गए, इसलिए मैंने इसे रोक लिया। नया उत्तर पाने के लिए फिर से पूछें। अगर आपने मुझसे कुछ बदलने को कहा था, तो पहले उसे देख लें।",
  },
  // A background task's failed answer (buildFailedJobCompletion). The
  // non-English lines were written with the English source on 2026-10-20 and
  // have not had a native review yet.
  task_failed_error: {
    en: "I couldn't finish that. Please try again.",
    es: "No pude terminar eso. Inténtalo de nuevo.",
    fr: "Je n'ai pas pu terminer cela. Veuillez réessayer.",
    de: "Das konnte ich nicht abschließen. Bitte versuchen Sie es noch einmal.",
    it: "Non sono riuscito a completarlo. Riprova.",
    pt: "Não consegui concluir isso. Tente novamente.",
    zh: "我没能完成这项操作。请重试。",
    ja: "完了できませんでした。もう一度お試しください。",
    ko: "완료하지 못했습니다. 다시 시도해 주세요.",
    ru: "Не удалось это завершить. Попробуйте еще раз.",
    ar: "لم أتمكن من إنهاء ذلك. يُرجى المحاولة مرة أخرى.",
    hi: "मैं इसे पूरा नहीं कर सका। कृपया फिर से कोशिश करें।",
  },
  task_view_failed_error: {
    en: "I couldn't open that view. Please try again.",
    es: "No pude abrir esa vista. Inténtalo de nuevo.",
    fr: "Je n'ai pas pu ouvrir cette vue. Veuillez réessayer.",
    de: "Diese Ansicht konnte ich nicht öffnen. Bitte versuchen Sie es noch einmal.",
    it: "Non sono riuscito ad aprire quella vista. Riprova.",
    pt: "Não consegui abrir essa visualização. Tente novamente.",
    zh: "我没能打开该视图。请重试。",
    ja: "その画面を開けませんでした。もう一度お試しください。",
    ko: "해당 화면을 열지 못했습니다. 다시 시도해 주세요.",
    ru: "Не удалось открыть этот экран. Попробуйте еще раз.",
    ar: "لم أتمكن من فتح هذا العرض. يُرجى المحاولة مرة أخرى.",
    hi: "मैं वह व्यू नहीं खोल सका। कृपया फिर से कोशिश करें।",
  },
  task_timed_out_error: {
    en: "That took too long and stopped. Please ask again.",
    es: "Eso tardó demasiado y se detuvo. Vuelve a pedirlo.",
    fr: "Cela a pris trop de temps et s'est arrêté. Veuillez redemander.",
    de: "Das hat zu lange gedauert und wurde abgebrochen. Bitte fragen Sie noch einmal.",
    it: "Ci è voluto troppo tempo e si è interrotto. Chiedi di nuovo.",
    pt: "Isso demorou demais e foi interrompido. Peça novamente.",
    zh: "耗时太长，已停止。请重新提问。",
    ja: "時間がかかりすぎたため停止しました。もう一度お尋ねください。",
    ko: "시간이 너무 오래 걸려 중단되었습니다. 다시 요청해 주세요.",
    ru: "Это заняло слишком много времени и было остановлено. Спросите еще раз.",
    ar: "استغرق ذلك وقتًا طويلًا وتوقف. يُرجى الطلب مرة أخرى.",
    hi: "इसमें बहुत समय लगा और यह रुक गया। कृपया फिर से पूछें।",
  },
  task_cancelled_error: {
    en: "That request was cancelled before it finished. You can try again when you're ready.",
    es: "Esa solicitud se canceló antes de terminar. Puedes intentarlo de nuevo cuando quieras.",
    fr: "Cette demande a été annulée avant de se terminer. Vous pourrez réessayer quand vous le souhaitez.",
    de: "Diese Anfrage wurde abgebrochen, bevor sie fertig war. Sie können es erneut versuchen, wenn Sie bereit sind.",
    it: "Quella richiesta è stata annullata prima di finire. Puoi riprovare quando vuoi.",
    pt: "Essa solicitação foi cancelada antes de terminar. Você pode tentar de novo quando quiser.",
    zh: "该请求在完成前已被取消。准备好后可以再试一次。",
    ja: "このリクエストは完了前にキャンセルされました。準備ができたらもう一度お試しください。",
    ko: "이 요청은 완료되기 전에 취소되었습니다. 준비되면 다시 시도해 주세요.",
    ru: "Этот запрос отменили до завершения. Можно попробовать снова, когда будете готовы.",
    ar: "أُلغي هذا الطلب قبل أن يكتمل. يمكنك المحاولة مرة أخرى عندما تكون مستعدًا.",
    hi: "यह अनुरोध पूरा होने से पहले रद्द हो गया। जब आप तैयार हों, फिर से कोशिश कर सकते हैं।",
  },
  /** One failed task's line, named by its task: "Shopping list: …". */
  named_task_line: {
    en: "{task}: {line}",
    es: "{task}: {line}",
    fr: "{task} : {line}",
    de: "{task}: {line}",
    it: "{task}: {line}",
    pt: "{task}: {line}",
    zh: "{task}：{line}",
    ja: "{task}：{line}",
    ko: "{task}: {line}",
    ru: "{task}: {line}",
    ar: "{task}: {line}",
    hi: "{task}: {line}",
  },
  async_results_ready: {
    en: "I've processed your request and gathered the results.",
    es: "He procesado tu solicitud y reunido los resultados.",
    fr: "J'ai traité votre demande et rassemblé les résultats.",
    de: "Ich habe Ihre Anfrage verarbeitet und die Ergebnisse zusammengestellt.",
    it: "Ho elaborato la tua richiesta e raccolto i risultati.",
    pt: "Processei sua solicitação e reuni os resultados.",
    zh: "我已经处理了你的请求，并整理好了结果。",
    ja: "リクエストを処理し、結果をまとめました。",
    ko: "요청을 처리하고 결과를 정리했습니다.",
    ru: "Я обработал ваш запрос и собрал результаты.",
    ar: "لقد عالجت طلبك وجمعت النتائج.",
    hi: "मैंने आपके अनुरोध को प्रोसेस कर लिया है और परिणाम एकत्र कर लिए हैं।",
  },
  opened_requested_component: {
    en: "I've opened that for you.",
    es: "Ya lo abrí para ti.",
    fr: "Je l'ai ouvert pour vous.",
    de: "Ich habe das für Sie geöffnet.",
    it: "L'ho aperto per te.",
    pt: "Já abri isso para você.",
    zh: "我已经帮你打开了。",
    ja: "開いておきました。",
    ko: "열어 두었습니다.",
    ru: "Я уже открыл это для вас.",
    ar: "لقد فتحته لك.",
    hi: "मैंने वह आपके लिए खोल दिया है।",
  },
  closed_requested_component: {
    en: "I've closed that for you.",
    es: "Ya lo cerré para ti.",
    fr: "Je l'ai fermé pour vous.",
    de: "Ich habe das für Sie geschlossen.",
    it: "L'ho chiuso per te.",
    pt: "Já fechei isso para você.",
    zh: "我已经帮你关掉了。",
    ja: "閉じておきました。",
    ko: "닫아 두었습니다.",
    ru: "Я уже закрыл это для вас.",
    ar: "لقد أغلقته لك.",
    hi: "मैंने वह आपके लिए बंद कर दिया है।",
  },
  view_already_clear: {
    en: "Your view is already clear.",
    es: "Tu vista ya está despejada.",
    fr: "Votre vue est déjà dégagée.",
    de: "Ihre Ansicht ist bereits frei.",
    it: "La tua vista è già libera.",
    pt: "Sua visualização já está limpa.",
    zh: "你的视图已经清空了。",
    ja: "ビューはすでにクリアです。",
    ko: "이미 화면이 정리되어 있습니다.",
    ru: "Ваш экран уже очищен.",
    ar: "العرض لديك واضح بالفعل.",
    hi: "आपका व्यू पहले से साफ़ है।",
  },
  protected_component_cannot_close: {
    en: "That is part of the core interface, so I can't close it.",
    es: "Eso forma parte de la interfaz principal, así que no puedo cerrarlo.",
    fr: "Cela fait partie de l'interface principale, je ne peux donc pas le fermer.",
    de: "Das gehört zur Kernoberfläche, daher kann ich es nicht schließen.",
    it: "Fa parte dell'interfaccia principale, quindi non posso chiuderlo.",
    pt: "Isso faz parte da interface principal, então não posso fechá-lo.",
    zh: "那是核心界面的一部分，所以我不能关闭它。",
    ja: "それは中核インターフェースの一部なので閉じられません。",
    ko: "그것은 핵심 인터페이스의 일부라 닫을 수 없습니다.",
    ru: "Это часть основного интерфейса, поэтому я не могу закрыть её.",
    ar: "هذا جزء من الواجهة الأساسية، لذلك لا يمكنني إغلاقه.",
    hi: "यह मुख्य इंटरफ़ेस का हिस्सा है, इसलिए मैं इसे बंद नहीं कर सकता।",
  },
  enable_location_access: {
    en: "I'd love to help. Please enable location access.",
    es: "Con gusto te ayudo. Activa el acceso a la ubicación.",
    fr: "Je serais ravi de vous aider. Veuillez activer l'accès à la localisation.",
    de: "Ich helfe Ihnen gern. Bitte aktivieren Sie den Standortzugriff.",
    it: "Ti aiuto volentieri. Attiva l'accesso alla posizione.",
    pt: "Vou adorar ajudar. Ative o acesso à localização.",
    zh: "我很乐意帮你。请启用位置访问。",
    ja: "ぜひお手伝いします。位置情報へのアクセスを有効にしてください。",
    ko: "기꺼이 도와드릴게요. 위치 접근 권한을 켜 주세요.",
    ru: "С радостью помогу. Пожалуйста, включите доступ к геолокации.",
    ar: "يسعدني المساعدة. يرجى تفعيل الوصول إلى الموقع.",
    hi: "मैं खुशी से मदद करूँगा। कृपया लोकेशन एक्सेस चालू करें।",
  },
  capability_offer_to_team: {
    en: "Want me to pass this request to the Smart Manager team?",
    es: "¿Quieres que le pase esta solicitud al equipo de Smart Manager?",
    fr: "Voulez-vous que je transmette cette demande à l'équipe Smart Manager ?",
    de: "Soll ich diese Anfrage an das Smart Manager-Team weitergeben?",
    it: "Vuoi che inoltri questa richiesta al team di Smart Manager?",
    pt: "Quer que eu passe este pedido para a equipe do Smart Manager?",
    zh: "要我把这个请求转给 Smart Manager 团队吗？",
    ja: "このリクエストを Smart Manager チームに伝えましょうか？",
    ko: "이 요청을 Smart Manager 팀에 전달할까요?",
    ru: "Передать этот запрос команде Smart Manager?",
    ar: "هل تريد أن أنقل هذا الطلب إلى فريق Smart Manager؟",
    hi: "क्या मैं यह अनुरोध Smart Manager टीम तक पहुँचा दूँ?",
  },
  need_component_to_close: {
    en: "Tell me which visible component to close, or say to close everything.",
    es: "Dime qué componente visible quieres cerrar, o di que cierre todo.",
    fr: "Dites-moi quel composant visible fermer, ou demandez de tout fermer.",
    de: "Sagen Sie mir, welches sichtbare Element ich schließen soll, oder sagen Sie, dass ich alles schließen soll.",
    it: "Dimmi quale componente visibile chiudere, oppure chiedimi di chiudere tutto.",
    pt: "Diga qual componente visível você quer fechar, ou peça para fechar tudo.",
    zh: "请告诉我要关闭哪个可见组件，或者说关闭全部。",
    ja: "閉じたい表示中のコンポーネントを教えてください。すべて閉じることもできます。",
    ko: "닫을 표시 중인 컴포넌트를 알려 주거나, 전부 닫아 달라고 말해 주세요.",
    ru: "Скажите, какой видимый компонент закрыть, или попросите закрыть всё.",
    ar: "أخبرني بأي مكوّن ظاهر تريد إغلاقه، أو قل إنك تريد إغلاق كل شيء.",
    hi: "बताइए कौन-सा दिख रहा कंपोनेंट बंद करना है, या कहें कि सब कुछ बंद कर दूँ।",
  },
  need_ui_component_request: {
    en: "Tell me what UI component you want to open, edit, or close.",
    es: "Dime qué componente de la interfaz quieres abrir, editar o cerrar.",
    fr: "Dites-moi quel composant d'interface vous voulez ouvrir, modifier ou fermer.",
    de: "Sagen Sie mir, welches UI-Element Sie öffnen, bearbeiten oder schließen möchten.",
    it: "Dimmi quale componente dell'interfaccia vuoi aprire, modificare o chiudere.",
    pt: "Diga qual componente da interface você quer abrir, editar ou fechar.",
    zh: "请告诉我你想打开、编辑或关闭哪个界面组件。",
    ja: "開きたい、編集したい、または閉じたいUIコンポーネントを教えてください。",
    ko: "열고, 편집하고, 닫고 싶은 UI 컴포넌트를 알려 주세요.",
    ru: "Скажите, какой элемент интерфейса вы хотите открыть, изменить или закрыть.",
    ar: "أخبرني بأي مكوّن واجهة تريد فتحه أو تعديله أو إغلاقه.",
    hi: "मुझे बताइए कि कौन-सा UI कंपोनेंट खोलना, संपादित करना या बंद करना है।",
  },
  ambiguous_component_edit: {
    en: "I'm not sure which component you want to edit. Could you be more specific? I think it might be: {componentName}",
    es: "No estoy seguro de qué componente quieres editar. ¿Puedes ser más específico? Creo que podría ser: {componentName}",
    fr: "Je ne suis pas certain du composant que vous voulez modifier. Pouvez-vous préciser ? Je pense qu'il pourrait s'agir de : {componentName}",
    de: "Ich bin nicht sicher, welches Element Sie bearbeiten möchten. Können Sie genauer sein? Ich denke, es könnte sein: {componentName}",
    it: "Non sono sicuro di quale componente vuoi modificare. Puoi essere più specifico? Credo possa essere: {componentName}",
    pt: "Não tenho certeza de qual componente você quer editar. Pode ser mais específico? Acho que pode ser: {componentName}",
    zh: "我还不确定你想编辑哪个组件。可以再具体一点吗？我猜可能是：{componentName}",
    ja: "どのコンポーネントを編集したいのかまだはっきりしません。もう少し具体的に教えてください。候補は {componentName} かもしれません。",
    ko: "어떤 컴포넌트를 편집하려는지 확실하지 않습니다. 조금 더 구체적으로 말씀해 주세요. 아마 이 컴포넌트일 수 있습니다: {componentName}",
    ru: "Я не уверен, какой компонент вы хотите изменить. Можете уточнить? Возможно, это: {componentName}",
    ar: "لست متأكدًا من المكوّن الذي تريد تعديله. هل يمكنك التوضيح أكثر؟ أعتقد أنه قد يكون: {componentName}",
    hi: "मुझे पूरी तरह स्पष्ट नहीं है कि आप किस कंपोनेंट को संपादित करना चाहते हैं। क्या आप थोड़ा और स्पष्ट बता सकते हैं? मुझे लगता है यह हो सकता है: {componentName}",
  },
  componentbuilder_edit_blocked: {
    en: "That card follows a Component Studio request. Tell me what to change and I'll pass it on.",
    es: "Esa tarjeta sigue una solicitud a Component Studio. Dime qué quieres cambiar y se lo pasaré a Component Studio.",
    fr: "Cette carte suit une demande à Component Studio. Dites-moi ce qu'il faut changer et je le transmettrai à Component Studio.",
    de: "Diese Karte verfolgt eine Anfrage an Component Studio. Sag mir, was geändert werden soll, und ich gebe es an Component Studio weiter.",
    it: "Questa scheda segue una richiesta a Component Studio. Dimmi cosa cambiare e lo passerò a Component Studio.",
    pt: "Esse cartão acompanha um pedido ao Component Studio. Diga o que mudar e eu passo para o Component Studio.",
    zh: "这张卡片跟踪的是一个 Component Studio 请求。告诉我要改什么，我会转交给 Component Studio。",
    ja: "このカードは Component Studio へのリクエストを追跡しています。変更したい点を教えてください。Component Studio に伝えます。",
    ko: "이 카드는 Component Studio 요청을 추적합니다. 바꿀 내용을 말씀해 주시면 Component Studio에 전달할게요.",
    ru: "Эта карточка отслеживает запрос к Component Studio. Скажите, что изменить, и я передам это в Component Studio.",
    ar: "تتابع هذه البطاقة طلبًا إلى Component Studio. أخبرني بما تريد تغييره وسأنقله إلى Component Studio.",
    hi: "यह कार्ड Component Studio के एक अनुरोध को ट्रैक करता है। बताइए क्या बदलना है, मैं उसे Component Studio तक पहुँचा दूँगा।",
  },
  edit_request_rephrase: {
    en: "I had trouble processing your edit request. Could you try rephrasing it?",
    es: "Tuve problemas para procesar tu solicitud de edición. ¿Puedes reformularla?",
    fr: "J'ai eu du mal à traiter votre demande de modification. Pouvez-vous la reformuler ?",
    de: "Ich hatte Probleme, Ihre Bearbeitungsanfrage zu verarbeiten. Können Sie sie anders formulieren?",
    it: "Ho avuto difficoltà a elaborare la tua richiesta di modifica. Puoi riformularla?",
    pt: "Tive dificuldade para processar sua solicitação de edição. Pode reformulá-la?",
    zh: "我处理你的编辑请求时遇到了问题。可以换一种说法吗？",
    ja: "編集リクエストの処理に問題がありました。言い換えてもらえますか。",
    ko: "편집 요청을 처리하는 데 문제가 있었습니다. 다른 방식으로 말씀해 주시겠어요?",
    ru: "У меня возникли трудности с обработкой вашего запроса на изменение. Можете переформулировать его?",
    ar: "واجهت مشكلة في معالجة طلب التعديل. هل يمكنك إعادة صياغته؟",
    hi: "आपके संपादन अनुरोध को प्रोसेस करने में मुझे दिक्कत हुई। क्या आप इसे फिर से अलग तरीके से कह सकते हैं?",
  },
  component_undo_ready: {
      "en": "The saved edit is ready to undo in its original workspace.",
      "es": "El cambio guardado está listo para deshacerse en su espacio original.",
      "fr": "La modification enregistrée est prête à être annulée dans son espace d’origine.",
      "de": "Die gespeicherte Änderung kann im ursprünglichen Arbeitsbereich rückgängig gemacht werden.",
      "it": "La modifica salvata è pronta per essere annullata nello spazio originale.",
      "pt": "A alteração salva está pronta para ser desfeita no espaço original.",
      "zh": "已保存的更改可以在原工作区中撤销。",
      "ja": "保存された変更を元のワークスペースで元に戻す準備ができました。",
      "ko": "저장된 변경을 원래 작업 공간에서 실행 취소할 준비가 되었습니다.",
      "ru": "Сохранённое изменение готово к отмене в исходной рабочей области.",
      "ar": "التعديل المحفوظ جاهز للتراجع عنه في مساحة العمل الأصلية.",
      "hi": "सहेजे गए बदलाव को मूल कार्यक्षेत्र में पूर्ववत किया जा सकता है।"
  },
  component_undo_changed: {
      "en": "This view changed after that edit. I kept the newer changes.",
      "es": "Esta vista cambió después de esa edición. Conservé los cambios más recientes.",
      "fr": "Cette vue a changé depuis cette modification. Les changements plus récents ont été conservés.",
      "de": "Diese Ansicht wurde danach geändert. Die neueren Änderungen bleiben erhalten.",
      "it": "Questa vista è cambiata dopo quella modifica. Ho mantenuto le modifiche più recenti.",
      "pt": "Esta visualização mudou depois daquela edição. Mantive as alterações mais recentes.",
      "zh": "此视图在那次编辑后又发生了更改，已保留较新的更改。",
      "ja": "その編集後にビューが変更されています。新しい変更は保持しました。",
      "ko": "해당 편집 이후 이 보기가 변경되었습니다. 최신 변경 사항을 유지했습니다.",
      "ru": "После того редактирования вид изменился. Более новые изменения сохранены.",
      "ar": "تغير هذا العرض بعد ذلك التعديل. أبقيت التغييرات الأحدث.",
      "hi": "उस संपादन के बाद यह दृश्य बदल गया है। नए बदलाव सुरक्षित रखे गए हैं।"
  },
  component_undo_done: {
      "en": "The saved edit was undone.",
      "es": "Se deshizo el cambio guardado.",
      "fr": "La modification enregistrée a été annulée.",
      "de": "Die gespeicherte Änderung wurde rückgängig gemacht.",
      "it": "La modifica salvata è stata annullata.",
      "pt": "A alteração salva foi desfeita.",
      "zh": "已撤销保存的更改。",
      "ja": "保存された変更を元に戻しました。",
      "ko": "저장된 변경을 실행 취소했습니다.",
      "ru": "Сохранённое изменение отменено.",
      "ar": "تم التراجع عن التعديل المحفوظ.",
      "hi": "सहेजा गया बदलाव पूर्ववत कर दिया गया।"
  },
  component_undo_already_done: {
      "en": "That saved edit has already been undone.",
      "es": "Ese cambio guardado ya se había deshecho.",
      "fr": "Cette modification enregistrée a déjà été annulée.",
      "de": "Diese gespeicherte Änderung wurde bereits rückgängig gemacht.",
      "it": "Quella modifica salvata è già stata annullata.",
      "pt": "Essa alteração salva já foi desfeita.",
      "zh": "那次保存的更改已经撤销。",
      "ja": "その保存された変更はすでに元に戻されています。",
      "ko": "해당 저장된 변경은 이미 실행 취소되었습니다.",
      "ru": "Это сохранённое изменение уже отменено.",
      "ar": "تم التراجع عن ذلك التعديل المحفوظ بالفعل.",
      "hi": "वह सहेजा गया बदलाव पहले ही पूर्ववत किया जा चुका है।"
  },
  component_undo_unavailable: {
      "en": "I could not verify a saved edit to undo for this view.",
      "es": "No pude verificar un cambio guardado para deshacer en esta vista.",
      "fr": "Je n’ai pas pu vérifier de modification enregistrée à annuler pour cette vue.",
      "de": "Für diese Ansicht konnte keine gespeicherte Änderung zum Rückgängigmachen bestätigt werden.",
      "it": "Non ho potuto verificare una modifica salvata da annullare per questa vista.",
      "pt": "Não consegui verificar uma alteração salva para desfazer nesta visualização.",
      "zh": "无法验证此视图中可撤销的已保存更改。",
      "ja": "このビューで元に戻せる保存済みの変更を確認できませんでした。",
      "ko": "이 보기에서 실행 취소할 수 있는 저장된 변경을 확인하지 못했습니다.",
      "ru": "Не удалось проверить сохранённое изменение для отмены в этом виде.",
      "ar": "لم أتمكن من التحقق من تعديل محفوظ للتراجع عنه في هذا العرض.",
      "hi": "इस दृश्य के लिए पूर्ववत करने योग्य सहेजे गए बदलाव की पुष्टि नहीं हो सकी।"
  },
  component_updated: {
    en: "Done. I've updated the {componentName}. {summary}",
    es: "Listo. He actualizado {componentName}. {summary}",
    fr: "C'est fait. J'ai mis à jour {componentName}. {summary}",
    de: "Fertig. Ich habe {componentName} aktualisiert. {summary}",
    it: "Fatto. Ho aggiornato {componentName}. {summary}",
    pt: "Pronto. Atualizei {componentName}. {summary}",
    zh: "已完成。我已经更新了 {componentName}。{summary}",
    ja: "完了しました。{componentName} を更新しました。{summary}",
    ko: "완료했습니다. {componentName}을(를) 업데이트했습니다. {summary}",
    ru: "Готово. Я обновил {componentName}. {summary}",
    ar: "تمّ. لقد حدّثت {componentName}. {summary}",
    hi: "हो गया। मैंने {componentName} को अपडेट कर दिया है। {summary}",
  },
  component_edit_error: {
    en: "Sorry, I encountered an error while trying to edit the component. Please try again.",
    es: "Lo siento, encontré un error al intentar editar el componente. Inténtalo de nuevo.",
    fr: "Désolé, j'ai rencontré une erreur en essayant de modifier le composant. Veuillez réessayer.",
    de: "Entschuldigung, beim Bearbeiten der Komponente ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Mi dispiace, si è verificato un errore durante la modifica del componente. Riprova.",
    pt: "Desculpe, encontrei um erro ao tentar editar o componente. Tente novamente.",
    zh: "抱歉，编辑组件时出错了。请再试一次。",
    ja: "申し訳ありません。コンポーネントの編集中にエラーが発生しました。もう一度お試しください。",
    ko: "죄송합니다. 컴포넌트를 편집하는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "Извините, при попытке изменить компонент произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "عذرًا، حدث خطأ أثناء محاولة تعديل المكوّن. يرجى المحاولة مرة أخرى.",
    hi: "माफ़ कीजिए, कंपोनेंट को संपादित करते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  need_communication_action: {
    en: "Tell me whether you want to check notifications, start or join a video call, make a phone call, or send an SMS. For writing drafts, use text_editor.",
    es: "Dime si quieres revisar notificaciones, iniciar o unirte a una videollamada, hacer una llamada telefónica o enviar un SMS. Para redactar borradores, usa text_editor.",
    fr: "Dites-moi si vous voulez vérifier les notifications, démarrer ou rejoindre un appel vidéo, passer un appel téléphonique ou envoyer un SMS. Pour rédiger des brouillons, utilisez text_editor.",
    de: "Sagen Sie mir, ob Sie Benachrichtigungen prüfen, einen Videoanruf starten oder beitreten, einen Telefonanruf tätigen oder eine SMS senden möchten. Zum Schreiben von Entwürfen verwenden Sie text_editor.",
    it: "Dimmi se vuoi controllare le notifiche, avviare o partecipare a una videochiamata, fare una chiamata o inviare un SMS. Per scrivere bozze, usa text_editor.",
    pt: "Diga se você quer verificar notificações, iniciar ou entrar em uma videochamada, fazer uma ligação ou enviar um SMS. Para escrever rascunhos, use text_editor.",
    zh: "请告诉我你是想查看通知、开始或加入视频通话、拨打电话，还是发送短信。若要撰写草稿，请使用 text_editor。",
    ja: "通知を確認したいのか、ビデオ通話を開始または参加したいのか、電話をかけたいのか、SMS を送りたいのかを教えてください。下書き作成には text_editor を使ってください。",
    ko: "알림 확인, 화상 통화 시작 또는 참여, 전화 걸기, SMS 보내기 중 무엇을 원하는지 알려 주세요. 초안 작성은 text_editor를 사용하세요.",
    ru: "Скажите, хотите ли вы проверить уведомления, начать или присоединиться к видеозвонку, совершить телефонный звонок или отправить SMS. Для черновиков используйте text_editor.",
    ar: "أخبرني هل تريد التحقق من الإشعارات، أو بدء مكالمة فيديو أو الانضمام إليها، أو إجراء مكالمة هاتفية، أو إرسال رسالة نصية. ولصياغة المسودات استخدم text_editor.",
    hi: "मुझे बताइए कि आप नोटिफिकेशन देखना चाहते हैं, वीडियो कॉल शुरू या जॉइन करना चाहते हैं, फोन कॉल करना चाहते हैं, या SMS भेजना चाहते हैं। ड्राफ्ट लिखने के लिए text_editor का उपयोग करें।",
  },
  confirm_new_recipient: {
    en: "I haven't contacted {recipient}: they weren't named in your request or the approved action. Tell me to contact {recipient} if that's who you mean.",
    es: "No he contactado a {recipient}: no aparecía en tu solicitud ni en la acción aprobada. Dime que contacte a {recipient} si es a quien te refieres.",
    fr: "Je n'ai pas contacté {recipient} : cette personne ne figurait ni dans votre demande ni dans l'action approuvée. Dites-moi de contacter {recipient} si c'est bien votre intention.",
    de: "Ich habe {recipient} nicht kontaktiert: Diese Person stand weder in Ihrer Anfrage noch in der genehmigten Aktion. Sagen Sie mir, dass ich {recipient} kontaktieren soll, wenn Sie das möchten.",
    it: "Non ho contattato {recipient}: non era nella tua richiesta né nell'azione approvata. Dimmi di contattare {recipient} se è la persona giusta.",
    pt: "Não entrei em contato com {recipient}: essa pessoa não estava no seu pedido nem na ação aprovada. Diga para eu contatar {recipient} se for quem você quer.",
    zh: "我尚未联系 {recipient}：此人未出现在你的请求或已批准的操作中。如果你想联系此人，请明确告诉我。",
    ja: "{recipient} にはまだ連絡していません。あなたの依頼にも承認済みの操作にも含まれていませんでした。連絡したい場合は明示してください。",
    ko: "{recipient}에게 연락하지 않았습니다. 요청이나 승인된 작업에 포함되지 않았습니다. 연락을 원한다면 명시해 주세요.",
    ru: "Я не связался с {recipient}: этого адресата не было ни в вашем запросе, ни в одобренном действии. Если вы хотите связаться с {recipient}, скажите об этом прямо.",
    ar: "لم أتواصل مع {recipient}: لم يكن ضمن طلبك أو الإجراء المعتمد. أخبرني صراحة إذا أردت التواصل مع {recipient}.",
    hi: "मैंने {recipient} से संपर्क नहीं किया: वे आपके अनुरोध या स्वीकृत कार्रवाई में नहीं थे। अगर आप उनसे संपर्क करना चाहते हैं तो स्पष्ट रूप से बताएं।",
  },
  workflow_recipient_not_approved: {
    en: "I didn't send this. This workflow wasn't set up to contact this person, so nothing went out. To include them, change the workflow or ask me directly.",
    es: "No lo envié. Este flujo de trabajo no estaba configurado para contactar a esta persona, así que no salió nada. Para incluirla, cambia el flujo o pídemelo directamente.",
    fr: "Je ne l'ai pas envoyé. Ce workflow n'était pas prévu pour contacter cette personne, donc rien n'est parti. Pour l'inclure, modifiez le workflow ou demandez-le-moi directement.",
    de: "Ich habe das nicht gesendet. Dieser Workflow war nicht dafür eingerichtet, diese Person zu kontaktieren, daher ging nichts raus. Um sie einzuschließen, ändern Sie den Workflow oder fragen Sie mich direkt.",
    it: "Non l'ho inviato. Questo flusso di lavoro non era impostato per contattare questa persona, quindi non è partito nulla. Per includerla, modifica il flusso o chiedimelo direttamente.",
    pt: "Não enviei. Este fluxo de trabalho não foi configurado para contatar essa pessoa, então nada saiu. Para incluí-la, altere o fluxo ou peça diretamente para mim.",
    zh: "我没有发送。此工作流未设置为联系此人，因此没有发出任何内容。如需包含此人，请修改工作流或直接告诉我。",
    ja: "送信していません。このワークフローはこの相手に連絡する設定ではないため、何も送られていません。含めたい場合は、ワークフローを変更するか、直接依頼してください。",
    ko: "보내지 않았습니다. 이 워크플로는 이 사람에게 연락하도록 설정되지 않아 아무것도 전송되지 않았습니다. 포함하려면 워크플로를 변경하거나 직접 요청해 주세요.",
    ru: "Я это не отправил. Этот сценарий не был настроен на связь с этим человеком, поэтому ничего не ушло. Чтобы включить его, измените сценарий или попросите меня напрямую.",
    ar: "لم أرسل هذا. لم يُضبط سير العمل هذا للتواصل مع هذا الشخص، لذلك لم يُرسل شيء. لإضافته، عدّل سير العمل أو اطلب مني ذلك مباشرة.",
    hi: "मैंने इसे नहीं भेजा। यह वर्कफ़्लो इस व्यक्ति से संपर्क करने के लिए सेट नहीं था, इसलिए कुछ नहीं गया। उन्हें शामिल करने के लिए वर्कफ़्लो बदलें या मुझसे सीधे कहें।",
  },
  confirm_message_text: {
    en: "I haven't prepared that text: the message \"{message}\" wasn't in your request or the approved action. Tell me the exact words to send if that's what you mean.",
    es: "No he preparado ese mensaje: el texto \"{message}\" no estaba en tu solicitud ni en la acción aprobada. Dime las palabras exactas que quieres enviar si es lo que quieres.",
    fr: "Je n'ai pas préparé ce SMS : le message « {message} » ne figurait ni dans votre demande ni dans l'action approuvée. Dites-moi les mots exacts à envoyer si c'est bien votre intention.",
    de: "Ich habe diese Nachricht nicht vorbereitet: Der Text „{message}“ stand weder in Ihrer Anfrage noch in der genehmigten Aktion. Nennen Sie mir den genauen Wortlaut, wenn Sie das senden möchten.",
    it: "Non ho preparato quel messaggio: il testo \"{message}\" non era nella tua richiesta né nell'azione approvata. Dimmi le parole esatte da inviare se è ciò che vuoi.",
    pt: "Não preparei essa mensagem: o texto \"{message}\" não estava no seu pedido nem na ação aprovada. Diga as palavras exatas a enviar se for isso que você quer.",
    zh: "我尚未准备这条短信：消息“{message}”不在你的请求或已批准的操作中。如果你确实想发送，请告诉我确切内容。",
    ja: "そのメッセージはまだ用意していません。「{message}」はあなたの依頼にも承認済みの操作にも含まれていませんでした。送信したい場合は正確な文面を教えてください。",
    ko: "그 문자는 준비하지 않았습니다. \"{message}\" 메시지는 요청이나 승인된 작업에 포함되지 않았습니다. 보내려는 정확한 문구를 알려 주세요.",
    ru: "Я не подготовил это сообщение: текста «{message}» не было ни в вашем запросе, ни в одобренном действии. Если вы хотите его отправить, скажите точный текст.",
    ar: "لم أجهز هذه الرسالة: النص \"{message}\" لم يكن ضمن طلبك أو الإجراء المعتمد. أخبرني بالكلمات الدقيقة التي تريد إرسالها إن كان هذا ما تقصده.",
    hi: "मैंने वह संदेश तैयार नहीं किया: \"{message}\" आपके अनुरोध या स्वीकृत कार्रवाई में नहीं था। अगर आप यही भेजना चाहते हैं तो सटीक शब्द बताएं।",
  },
  notifications_error: {
    en: "I ran into an error while getting notifications. Please try again.",
    es: "Encontré un error al obtener las notificaciones. Inténtalo de nuevo.",
    fr: "J'ai rencontré une erreur lors de la récupération des notifications. Veuillez réessayer.",
    de: "Beim Abrufen der Benachrichtigungen ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Si è verificato un errore durante il recupero delle notifiche. Riprova.",
    pt: "Encontrei um erro ao buscar as notificações. Tente novamente.",
    zh: "获取通知时出错了。请再试一次。",
    ja: "通知の取得中にエラーが発生しました。もう一度お試しください。",
    ko: "알림을 가져오는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "При получении уведомлений произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "حدث خطأ أثناء جلب الإشعارات. يرجى المحاولة مرة أخرى.",
    hi: "नोटिफिकेशन लाते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  no_new_notifications_today: {
    en: "No new notifications for today.",
    es: "Sin notificaciones nuevas para hoy.",
    fr: "Aucune nouvelle notification pour aujourd'hui.",
    de: "Für heute wurden keine neuen Benachrichtigungen gefunden.",
    it: "Non sono state trovate nuove notifiche per oggi.",
    pt: "Nenhuma nova notificação foi encontrada para hoje.",
    zh: "今天没有发现新的通知。",
    ja: "今日の新しい通知はありません。",
    ko: "오늘 새로운 알림이 없습니다.",
    ru: "На сегодня новых уведомлений не найдено.",
    ar: "لم يتم العثور على إشعارات جديدة لليوم.",
    hi: "आज के लिए कोई नई नोटिफिकेशन नहीं मिली।",
  },
  notifications_digest_one: {
    en: "You have 1 notification. It's on screen.",
    es: "Tienes 1 notificación. Está en pantalla.",
    fr: "Vous avez 1 notification. Elle est à l'écran.",
    de: "Du hast 1 Benachrichtigung. Sie ist auf dem Bildschirm.",
    it: "Hai 1 notifica. È sullo schermo.",
    pt: "Você tem 1 notificação. Ela está na tela.",
    zh: "你有 1 条通知，已显示在屏幕上。",
    ja: "通知が1件あります。画面に表示しています。",
    ko: "알림이 1개 있습니다. 화면에 표시했습니다.",
    ru: "У вас 1 уведомление. Оно на экране.",
    ar: "لديك إشعار واحد. إنه معروض على الشاشة.",
    hi: "आपके पास 1 नोटिफिकेशन है। यह स्क्रीन पर है।",
  },
  notifications_digest_many: {
    en: "You have {count} notifications. They're on screen.",
    es: "Tienes {count} notificaciones. Están en pantalla.",
    fr: "Vous avez {count} notifications. Elles sont à l'écran.",
    de: "Du hast {count} Benachrichtigungen. Sie sind auf dem Bildschirm.",
    it: "Hai {count} notifiche. Sono sullo schermo.",
    pt: "Você tem {count} notificações. Elas estão na tela.",
    zh: "你有 {count} 条通知，已显示在屏幕上。",
    ja: "通知が{count}件あります。画面に表示しています。",
    ko: "알림이 {count}개 있습니다. 화면에 표시했습니다.",
    ru: "Уведомлений: {count}. Они на экране.",
    ar: "لديك {count} إشعارات. إنها معروضة على الشاشة.",
    hi: "आपके पास {count} नोटिफिकेशन हैं। वे स्क्रीन पर हैं।",
  },
  notifications_digest_action_one: {
    en: "1 needs you.",
    es: "1 necesita tu atención.",
    fr: "1 attend votre réponse.",
    de: "1 braucht dich.",
    it: "1 richiede la tua attenzione.",
    pt: "1 precisa de você.",
    zh: "其中 1 条需要你处理。",
    ja: "1件は対応が必要です。",
    ko: "1개는 확인이 필요합니다.",
    ru: "1 ждёт вашего решения.",
    ar: "واحد منها يحتاج إليك.",
    hi: "1 को आपकी ज़रूरत है।",
  },
  notifications_digest_action_many: {
    en: "{count} need you.",
    es: "{count} necesitan tu atención.",
    fr: "{count} attendent votre réponse.",
    de: "{count} brauchen dich.",
    it: "{count} richiedono la tua attenzione.",
    pt: "{count} precisam de você.",
    zh: "其中 {count} 条需要你处理。",
    ja: "{count}件は対応が必要です。",
    ko: "{count}개는 확인이 필요합니다.",
    ru: "{count} ждут вашего решения.",
    ar: "{count} منها تحتاج إليك.",
    hi: "{count} को आपकी ज़रूरत है।",
  },
  feed_item_email: {
    en: "New email.",
    es: "Nuevo correo.",
    fr: "Nouvel e-mail.",
    de: "Neue E-Mail.",
    it: "Nuova email.",
    pt: "Novo e-mail.",
    zh: "新邮件。",
    ja: "新しいメールです。",
    ko: "새 이메일입니다.",
    ru: "Новое письмо.",
    ar: "بريد إلكتروني جديد.",
    hi: "नया ईमेल।",
  },
  feed_item_message: {
    en: "New message.",
    es: "Nuevo mensaje.",
    fr: "Nouveau message.",
    de: "Neue Nachricht.",
    it: "Nuovo messaggio.",
    pt: "Nova mensagem.",
    zh: "新消息。",
    ja: "新しいメッセージです。",
    ko: "새 메시지입니다.",
    ru: "Новое сообщение.",
    ar: "رسالة جديدة.",
    hi: "नया संदेश।",
  },
  feed_item_messages: {
    en: "{count} new messages.",
    es: "{count} mensajes nuevos.",
    fr: "{count} nouveaux messages.",
    de: "{count} neue Nachrichten.",
    it: "{count} nuovi messaggi.",
    pt: "{count} novas mensagens.",
    zh: "{count} 条新消息。",
    ja: "新しいメッセージが{count}件あります。",
    ko: "새 메시지 {count}개.",
    ru: "Новых сообщений: {count}.",
    ar: "{count} رسائل جديدة.",
    hi: "{count} नए संदेश।",
  },
  feed_item_message_on: {
    en: "New message on {platform}.",
    es: "Nuevo mensaje en {platform}.",
    fr: "Nouveau message sur {platform}.",
    de: "Neue Nachricht auf {platform}.",
    it: "Nuovo messaggio su {platform}.",
    pt: "Nova mensagem no {platform}.",
    zh: "{platform} 上有新消息。",
    ja: "{platform}に新しいメッセージがあります。",
    ko: "{platform}에 새 메시지가 있습니다.",
    ru: "Новое сообщение в {platform}.",
    ar: "رسالة جديدة على {platform}.",
    hi: "{platform} पर नया संदेश।",
  },
  feed_item_messages_on: {
    en: "{count} new messages on {platform}.",
    es: "{count} mensajes nuevos en {platform}.",
    fr: "{count} nouveaux messages sur {platform}.",
    de: "{count} neue Nachrichten auf {platform}.",
    it: "{count} nuovi messaggi su {platform}.",
    pt: "{count} novas mensagens no {platform}.",
    zh: "{platform} 上有 {count} 条新消息。",
    ja: "{platform}に新しいメッセージが{count}件あります。",
    ko: "{platform}에 새 메시지 {count}개가 있습니다.",
    ru: "Новых сообщений в {platform}: {count}.",
    ar: "{count} رسائل جديدة على {platform}.",
    hi: "{platform} पर {count} नए संदेश।",
  },
  feed_item_mention_on: {
    en: "You were mentioned on {platform}.",
    es: "Te mencionaron en {platform}.",
    fr: "Vous avez été mentionné sur {platform}.",
    de: "Du wurdest auf {platform} erwähnt.",
    it: "Sei stato menzionato su {platform}.",
    pt: "Você foi mencionado no {platform}.",
    zh: "有人在 {platform} 上提到了你。",
    ja: "{platform}であなたがメンションされました。",
    ko: "{platform}에서 회원님이 언급되었습니다.",
    ru: "Вас упомянули в {platform}.",
    ar: "تمت الإشارة إليك على {platform}.",
    hi: "{platform} पर आपका उल्लेख हुआ।",
  },
  feed_item_calendar: {
    en: "Your calendar changed.",
    es: "Tu calendario cambió.",
    fr: "Votre agenda a changé.",
    de: "Dein Kalender hat sich geändert.",
    it: "Il tuo calendario è cambiato.",
    pt: "Sua agenda mudou.",
    zh: "你的日历有变动。",
    ja: "カレンダーが変更されました。",
    ko: "캘린더가 변경되었습니다.",
    ru: "В вашем календаре изменения.",
    ar: "تغيّر تقويمك.",
    hi: "आपका कैलेंडर बदला है।",
  },
  feed_item_contact: {
    en: "A contact changed.",
    es: "Un contacto cambió.",
    fr: "Un contact a changé.",
    de: "Ein Kontakt hat sich geändert.",
    it: "Un contatto è cambiato.",
    pt: "Um contato mudou.",
    zh: "有联系人发生变动。",
    ja: "連絡先が変更されました。",
    ko: "연락처가 변경되었습니다.",
    ru: "Контакт изменился.",
    ar: "تغيّرت جهة اتصال.",
    hi: "एक संपर्क बदला है।",
  },
  feed_item_attachment: {
    en: "New attachment.",
    es: "Nuevo archivo adjunto.",
    fr: "Nouvelle pièce jointe.",
    de: "Neuer Anhang.",
    it: "Nuovo allegato.",
    pt: "Novo anexo.",
    zh: "新附件。",
    ja: "新しい添付ファイルです。",
    ko: "새 첨부 파일입니다.",
    ru: "Новое вложение.",
    ar: "مرفق جديد.",
    hi: "नया अटैचमेंट।",
  },
  feed_item_scout: {
    en: "A scout found an update.",
    es: "Un explorador encontró una novedad.",
    fr: "Un éclaireur a trouvé du nouveau.",
    de: "Ein Scout hat etwas Neues gefunden.",
    it: "Uno scout ha trovato una novità.",
    pt: "Um scout encontrou uma novidade.",
    zh: "侦察任务发现了新动态。",
    ja: "スカウトが更新を見つけました。",
    ko: "스카우트가 새 소식을 찾았습니다.",
    ru: "Скаут нашёл обновление.",
    ar: "عثر الكشّاف على تحديث.",
    hi: "स्काउट को एक अपडेट मिला।",
  },
  feed_item_update: {
    en: "New update from {vendor}.",
    es: "Novedad de {vendor}.",
    fr: "Nouvelle mise à jour de {vendor}.",
    de: "Neues von {vendor}.",
    it: "Novità da {vendor}.",
    pt: "Novidade de {vendor}.",
    zh: "来自 {vendor} 的新动态。",
    ja: "{vendor}から新しい更新があります。",
    ko: "{vendor}의 새 소식입니다.",
    ru: "Обновление от {vendor}.",
    ar: "تحديث جديد من {vendor}.",
    hi: "{vendor} से नया अपडेट।",
  },
  browser_service_not_configured: {
    en: "The browser automation service is not currently configured. Please contact support.",
    es: "El servicio de automatización del navegador no está configurado en este momento. Ponte en contacto con soporte.",
    fr: "Le service d'automatisation du navigateur n'est pas configuré pour le moment. Veuillez contacter le support.",
    de: "Der Browser-Automatisierungsdienst ist derzeit nicht konfiguriert. Bitte kontaktieren Sie den Support.",
    it: "Il servizio di automazione del browser non è attualmente configurato. Contatta il supporto.",
    pt: "O serviço de automação do navegador não está configurado no momento. Entre em contato com o suporte.",
    zh: "浏览器自动化服务当前尚未配置。请联系支持团队。",
    ja: "ブラウザー自動化サービスは現在設定されていません。サポートに連絡してください。",
    ko: "브라우저 자동화 서비스가 현재 설정되어 있지 않습니다. 지원팀에 문의해 주세요.",
    ru: "Сервис автоматизации браузера сейчас не настроен. Обратитесь в поддержку.",
    ar: "خدمة أتمتة المتصفح غير مهيأة حاليًا. يرجى التواصل مع الدعم.",
    hi: "ब्राउज़र ऑटोमेशन सेवा अभी कॉन्फ़िगर नहीं है। कृपया सपोर्ट से संपर्क करें।",
  },
  browser_task_required: {
    en: "What would you like me to do in the browser?",
    es: "¿Qué te gustaría que hiciera en el navegador?",
    fr: "Que souhaitez-vous que je fasse dans le navigateur ?",
    de: "Was soll ich im Browser für Sie tun?",
    it: "Che cosa vuoi che faccia nel browser?",
    pt: "O que você gostaria que eu fizesse no navegador?",
    zh: "你希望我在浏览器里做什么？",
    ja: "ブラウザーで何をしてほしいですか。",
    ko: "브라우저에서 무엇을 해드릴까요?",
    ru: "Что вы хотите, чтобы я сделал в браузере?",
    ar: "ما الذي تريدني أن أفعله في المتصفح؟",
    hi: "आप चाहते हैं कि मैं ब्राउज़र में क्या करूँ?",
  },
  scan_opened: {
    en: "Point your camera at it.",
    es: "Apunta la cámara hacia ello.",
    fr: "Pointez votre caméra dessus.",
    de: "Richten Sie Ihre Kamera darauf.",
    it: "Inquadralo con la fotocamera.",
    pt: "Aponte a câmera para isso.",
    zh: "请将摄像头对准它。",
    ja: "カメラを向けてください。",
    ko: "카메라를 비춰 주세요.",
    ru: "Наведите на это камеру.",
    ar: "وجّه الكاميرا نحوه.",
    hi: "अपना कैमरा उस पर रखें।",
  },
  link_check_ask: {
    en: "Send the link you want checked.",
    es: "Envía el enlace que quieres que revise.",
    fr: "Envoyez le lien que vous voulez vérifier.",
    de: "Senden Sie den Link, den ich prüfen soll.",
    it: "Inviami il link che vuoi controllare.",
    pt: "Envie o link que você quer verificar.",
    zh: "请发送你想检查的链接。",
    ja: "確認したいリンクを送ってください。",
    ko: "확인할 링크를 보내 주세요.",
    ru: "Пришлите ссылку, которую нужно проверить.",
    ar: "أرسل الرابط الذي تريد فحصه.",
    hi: "वह लिंक भेजें जिसे आप जाँचना चाहते हैं।",
  },
  link_check_failed: {
    en: "I couldn't check that link right now.",
    es: "No pude revisar ese enlace en este momento.",
    fr: "Je n'ai pas pu vérifier ce lien pour le moment.",
    de: "Ich konnte diesen Link gerade nicht prüfen.",
    it: "Non sono riuscito a controllare quel link in questo momento.",
    pt: "Não consegui verificar esse link agora.",
    zh: "我现在无法检查这个链接。",
    ja: "今はそのリンクを確認できませんでした。",
    ko: "지금은 그 링크를 확인할 수 없었어요.",
    ru: "Сейчас не удалось проверить эту ссылку.",
    ar: "لم أتمكن من فحص هذا الرابط الآن.",
    hi: "मैं अभी उस लिंक की जाँच नहीं कर सका।",
  },
  link_check_not_web: {
    en: "That isn't a web address, so there is no site to check.",
    es: "Eso no es una dirección web, así que no hay ningún sitio que revisar.",
    fr: "Ce n'est pas une adresse web, il n'y a donc aucun site à vérifier.",
    de: "Das ist keine Webadresse, also gibt es keine Website zu prüfen.",
    it: "Non è un indirizzo web, quindi non c'è nessun sito da controllare.",
    pt: "Isso não é um endereço da web, então não há site para verificar.",
    zh: "这不是网址，所以没有可检查的网站。",
    ja: "これはウェブアドレスではないため、確認するサイトがありません。",
    ko: "웹 주소가 아니라서 확인할 사이트가 없어요.",
    ru: "Это не веб-адрес, поэтому проверять нечего.",
    ar: "هذا ليس عنوان ويب، لذا لا يوجد موقع لفحصه.",
    hi: "यह वेब पता नहीं है, इसलिए जाँचने के लिए कोई साइट नहीं है।",
  },
  link_check_runs_code: {
    en: "Don't open this. It runs something on your device instead of opening a web page.",
    es: "No lo abras. Ejecuta algo en tu dispositivo en lugar de abrir una página web.",
    fr: "Ne l'ouvrez pas. Il exécute quelque chose sur votre appareil au lieu d'ouvrir une page web.",
    de: "Öffnen Sie das nicht. Es führt etwas auf Ihrem Gerät aus, statt eine Webseite zu öffnen.",
    it: "Non aprirlo. Esegue qualcosa sul tuo dispositivo invece di aprire una pagina web.",
    pt: "Não abra isso. Ele executa algo no seu dispositivo em vez de abrir uma página da web.",
    zh: "不要打开。它会在你的设备上运行某些内容，而不是打开网页。",
    ja: "開かないでください。ウェブページを開く代わりに、端末上で何かを実行します。",
    ko: "열지 마세요. 웹 페이지를 여는 대신 기기에서 무언가를 실행해요.",
    ru: "Не открывайте это. Оно запускает что-то на вашем устройстве вместо открытия веб-страницы.",
    ar: "لا تفتحه. فهو يشغّل شيئًا على جهازك بدلًا من فتح صفحة ويب.",
    hi: "इसे न खोलें। यह वेब पेज खोलने के बजाय आपके डिवाइस पर कुछ चलाता है।",
  },
  link_check_reported: {
    en: "Don't open this. People at {count} independent organizations reported it as a scam. No personal data was shared to tell you.",
    es: "No lo abras. Personas de {count} organizaciones independientes lo denunciaron como estafa. No se compartieron datos personales para avisarte.",
    fr: "Ne l'ouvrez pas. Des personnes de {count} organisations indépendantes l'ont signalé comme une arnaque. Aucune donnée personnelle n'a été partagée pour vous prévenir.",
    de: "Öffnen Sie das nicht. Personen aus {count} unabhängigen Organisationen haben es als Betrug gemeldet. Dafür wurden keine personenbezogenen Daten geteilt.",
    it: "Non aprirlo. Persone di {count} organizzazioni indipendenti lo hanno segnalato come truffa. Nessun dato personale è stato condiviso per avvisarti.",
    pt: "Não abra isso. Pessoas de {count} organizações independentes o denunciaram como golpe. Nenhum dado pessoal foi compartilhado para avisar você.",
    zh: "不要打开。来自 {count} 个独立组织的人举报它是诈骗。告知你这一点时没有共享任何个人数据。",
    ja: "開かないでください。{count} の独立した組織の人が詐欺として報告しています。お知らせするために個人データは共有されていません。",
    ko: "열지 마세요. {count}개 독립 조직의 사람들이 사기로 신고했어요. 이를 알려 드리는 데 개인 정보는 공유되지 않았어요.",
    ru: "Не открывайте это. Люди из {count} независимых организаций сообщили, что это мошенничество. Чтобы предупредить вас, личные данные не передавались.",
    ar: "لا تفتحه. أبلغ أشخاص من {count} مؤسسات مستقلة عنه كاحتيال. لم تُشارك أي بيانات شخصية لإبلاغك بذلك.",
    hi: "इसे न खोलें। {count} स्वतंत्र संगठनों के लोगों ने इसे घोटाला बताया है। आपको बताने के लिए कोई निजी डेटा साझा नहीं किया गया।",
  },
  link_check_listed: {
    en: "Don't open this. A security list marks {host} as unsafe.",
    es: "No lo abras. Una lista de seguridad marca {host} como no seguro.",
    fr: "Ne l'ouvrez pas. Une liste de sécurité signale {host} comme dangereux.",
    de: "Öffnen Sie das nicht. Eine Sicherheitsliste stuft {host} als unsicher ein.",
    it: "Non aprirlo. Un elenco di sicurezza segnala {host} come non sicuro.",
    pt: "Não abra isso. Uma lista de segurança marca {host} como inseguro.",
    zh: "不要打开。有安全列表将 {host} 标记为不安全。",
    ja: "開かないでください。セキュリティリストが {host} を危険と判定しています。",
    ko: "열지 마세요. 보안 목록에서 {host}을(를) 안전하지 않다고 표시해요.",
    ru: "Не открывайте это. Список безопасности отмечает {host} как небезопасный.",
    ar: "لا تفتحه. تصنّف قائمة أمان {host} على أنه غير آمن.",
    hi: "इसे न खोलें। एक सुरक्षा सूची {host} को असुरक्षित बताती है।",
  },
  link_check_danger: {
    en: "Don't open this. The address {host} has strong signs of a scam.",
    es: "No lo abras. La dirección {host} tiene señales claras de estafa.",
    fr: "Ne l'ouvrez pas. L'adresse {host} présente des signes nets d'arnaque.",
    de: "Öffnen Sie das nicht. Die Adresse {host} zeigt deutliche Anzeichen von Betrug.",
    it: "Non aprirlo. L'indirizzo {host} ha chiari segni di truffa.",
    pt: "Não abra isso. O endereço {host} tem sinais fortes de golpe.",
    zh: "不要打开。地址 {host} 有明显的诈骗迹象。",
    ja: "開かないでください。アドレス {host} には詐欺の強い兆候があります。",
    ko: "열지 마세요. 주소 {host}에 사기의 뚜렷한 징후가 있어요.",
    ru: "Не открывайте это. Адрес {host} имеет явные признаки мошенничества.",
    ar: "لا تفتحه. يحمل العنوان {host} علامات قوية على الاحتيال.",
    hi: "इसे न खोलें। पते {host} में घोटाले के साफ़ संकेत हैं।",
  },
  link_check_hidden: {
    en: "I couldn't see where this short link goes. Don't open it until you know who sent it.",
    es: "No pude ver adónde lleva este enlace corto. No lo abras hasta saber quién lo envió.",
    fr: "Je n'ai pas pu voir où mène ce lien court. Ne l'ouvrez pas avant de savoir qui l'a envoyé.",
    de: "Ich konnte nicht sehen, wohin dieser Kurzlink führt. Öffnen Sie ihn erst, wenn Sie wissen, wer ihn gesendet hat.",
    it: "Non sono riuscito a vedere dove porta questo link breve. Non aprirlo finché non sai chi lo ha inviato.",
    pt: "Não consegui ver para onde este link curto leva. Não o abra até saber quem o enviou.",
    zh: "我无法看到这个短链接指向哪里。在确认发送者之前不要打开它。",
    ja: "この短縮リンクの行き先を確認できませんでした。誰が送ったか分かるまで開かないでください。",
    ko: "이 단축 링크가 어디로 가는지 확인할 수 없었어요. 누가 보냈는지 알기 전에는 열지 마세요.",
    ru: "Не удалось увидеть, куда ведёт эта короткая ссылка. Не открывайте её, пока не узнаете, кто её прислал.",
    ar: "لم أتمكن من معرفة وجهة هذا الرابط المختصر. لا تفتحه حتى تعرف من أرسله.",
    hi: "मैं नहीं देख सका कि यह छोटा लिंक कहाँ ले जाता है। जब तक पता न हो कि इसे किसने भेजा, इसे न खोलें।",
  },
  link_check_caution: {
    en: "Check before you open {host}. It has warning signs.",
    es: "Revisa antes de abrir {host}. Tiene señales de advertencia.",
    fr: "Vérifiez avant d'ouvrir {host}. Il présente des signes d'alerte.",
    de: "Prüfen Sie {host}, bevor Sie es öffnen. Es gibt Warnzeichen.",
    it: "Controlla prima di aprire {host}. Ha segnali di allarme.",
    pt: "Verifique antes de abrir {host}. Ele tem sinais de alerta.",
    zh: "打开 {host} 之前请先核实。它有警示迹象。",
    ja: "{host} を開く前に確認してください。警告サインがあります。",
    ko: "{host}을(를) 열기 전에 확인하세요. 경고 신호가 있어요.",
    ru: "Проверьте, прежде чем открывать {host}. Есть тревожные признаки.",
    ar: "تحقّق قبل فتح {host}. توجد علامات تحذيرية.",
    hi: "{host} खोलने से पहले जाँच लें। इसमें चेतावनी के संकेत हैं।",
  },
  link_check_incomplete: {
    en: "I couldn't finish checking {host}.",
    es: "No pude terminar de revisar {host}.",
    fr: "Je n'ai pas pu terminer la vérification de {host}.",
    de: "Ich konnte die Prüfung von {host} nicht abschließen.",
    it: "Non sono riuscito a completare il controllo di {host}.",
    pt: "Não consegui terminar de verificar {host}.",
    zh: "我没能完成对 {host} 的检查。",
    ja: "{host} の確認を完了できませんでした。",
    ko: "{host} 확인을 끝내지 못했어요.",
    ru: "Не удалось до конца проверить {host}.",
    ar: "لم أتمكن من إكمال فحص {host}.",
    hi: "मैं {host} की जाँच पूरी नहीं कर सका।",
  },
  link_check_no_known: {
    en: "No known problems with {host}.",
    es: "No hay problemas conocidos con {host}.",
    fr: "Aucun problème connu avec {host}.",
    de: "Keine bekannten Probleme mit {host}.",
    it: "Nessun problema noto con {host}.",
    pt: "Nenhum problema conhecido com {host}.",
    zh: "{host} 没有已知问题。",
    ja: "{host} に既知の問題はありません。",
    ko: "{host}에 알려진 문제가 없어요.",
    ru: "Известных проблем с {host} нет.",
    ar: "لا توجد مشكلات معروفة في {host}.",
    hi: "{host} के साथ कोई ज्ञात समस्या नहीं है।",
  },
  link_check_commons_clear: {
    en: "No one in the network has reported it as a scam.",
    es: "Nadie en la red lo ha denunciado como estafa.",
    fr: "Personne dans le réseau ne l'a signalé comme une arnaque.",
    de: "Niemand im Netzwerk hat es als Betrug gemeldet.",
    it: "Nessuno nella rete lo ha segnalato come truffa.",
    pt: "Ninguém na rede o denunciou como golpe.",
    zh: "网络中没有人举报它是诈骗。",
    ja: "ネットワークで詐欺として報告した人はいません。",
    ko: "네트워크에서 사기로 신고한 사람은 없어요.",
    ru: "Никто в сети не сообщал, что это мошенничество.",
    ar: "لم يبلغ أحد في الشبكة عنه كاحتيال.",
    hi: "नेटवर्क में किसी ने इसे घोटाला नहीं बताया है।",
  },
  link_check_commons_unavailable: {
    en: "I couldn't reach scam reports from other people right now, so I can't say more.",
    es: "Ahora no pude consultar las denuncias de estafa de otras personas, así que no puedo decir más.",
    fr: "Je n'ai pas pu consulter les signalements d'arnaque d'autres personnes pour le moment, je ne peux donc pas en dire plus.",
    de: "Ich konnte die Betrugsmeldungen anderer gerade nicht abrufen, daher kann ich nicht mehr sagen.",
    it: "Al momento non riesco a consultare le segnalazioni di truffa di altre persone, quindi non posso dire di più.",
    pt: "Não consegui acessar as denúncias de golpe de outras pessoas agora, então não posso dizer mais.",
    zh: "我现在无法获取其他人的诈骗举报，所以无法提供更多信息。",
    ja: "今は他の人からの詐欺報告を確認できないため、これ以上は分かりません。",
    ko: "지금은 다른 사람들의 사기 신고를 확인할 수 없어 더 말씀드릴 수 없어요.",
    ru: "Сейчас не удалось получить сообщения о мошенничестве от других людей, поэтому больше сказать не могу.",
    ar: "تعذّر الوصول إلى بلاغات الاحتيال من الآخرين الآن، لذا لا يمكنني قول المزيد.",
    hi: "मैं अभी दूसरों की घोटाला रिपोर्ट तक नहीं पहुँच सका, इसलिए इससे ज़्यादा नहीं बता सकता।",
  },
  link_check_commons_not_shared: {
    en: "Scam reports from other people are checked only when your account shares with the network.",
    es: "Las denuncias de estafa de otras personas solo se consultan cuando tu cuenta comparte con la red.",
    fr: "Les signalements d'arnaque d'autres personnes ne sont vérifiés que si votre compte partage avec le réseau.",
    de: "Betrugsmeldungen anderer werden nur geprüft, wenn Ihr Konto mit dem Netzwerk teilt.",
    it: "Le segnalazioni di truffa di altre persone vengono controllate solo se il tuo account condivide con la rete.",
    pt: "As denúncias de golpe de outras pessoas só são verificadas quando sua conta compartilha com a rede.",
    zh: "只有当你的账户与网络共享时，才会检查其他人的诈骗举报。",
    ja: "他の人からの詐欺報告は、アカウントがネットワークと共有している場合にのみ確認されます。",
    ko: "다른 사람들의 사기 신고는 계정이 네트워크와 공유할 때만 확인돼요.",
    ru: "Сообщения о мошенничестве от других людей проверяются, только если ваш аккаунт делится данными с сетью.",
    ar: "لا تُفحص بلاغات الاحتيال من الآخرين إلا عندما يشارك حسابك مع الشبكة.",
    hi: "दूसरों की घोटाला रिपोर्ट तभी जाँची जाती हैं जब आपका खाता नेटवर्क के साथ साझा करता है।",
  },
  link_check_commons_not_applicable: {
    en: "It is a large shared site, so scam reports don't cover its own pages.",
    es: "Es un sitio grande y compartido, así que las denuncias de estafa no cubren sus propias páginas.",
    fr: "C'est un grand site partagé, les signalements d'arnaque ne couvrent donc pas ses propres pages.",
    de: "Es ist eine große gemeinsam genutzte Website, daher decken Betrugsmeldungen ihre eigenen Seiten nicht ab.",
    it: "È un grande sito condiviso, quindi le segnalazioni di truffa non coprono le sue pagine.",
    pt: "É um site grande e compartilhado, então as denúncias de golpe não cobrem as páginas dele.",
    zh: "这是一个大型共享网站，因此诈骗举报不涵盖它自己的页面。",
    ja: "大規模な共有サイトのため、詐欺報告はそのサイト自体のページを対象としません。",
    ko: "대형 공유 사이트라서 사기 신고가 그 사이트 자체 페이지에는 적용되지 않아요.",
    ru: "Это крупный общий сайт, поэтому сообщения о мошенничестве не охватывают его собственные страницы.",
    ar: "إنه موقع مشترك كبير، لذا لا تشمل بلاغات الاحتيال صفحاته الخاصة.",
    hi: "यह एक बड़ी साझा साइट है, इसलिए घोटाला रिपोर्ट इसके अपने पेजों पर लागू नहीं होतीं।",
  },
  browser_task_started: {
    en: "I've started the browser task. You can watch it live in the browser view.",
    es: "He iniciado la tarea del navegador. Puedes verla en vivo en la vista del navegador.",
    fr: "J'ai lancé la tâche dans le navigateur. Vous pouvez la suivre en direct dans la vue du navigateur.",
    de: "Ich habe die Browseraufgabe gestartet. Sie können sie live in der Browseransicht verfolgen.",
    it: "Ho avviato l'attività nel browser. Puoi seguirla dal vivo nella vista del browser.",
    pt: "Iniciei a tarefa no navegador. Você pode acompanhá-la ao vivo na visualização do navegador.",
    zh: "我已启动浏览器任务。你可以在浏览器视图中实时查看。",
    ja: "ブラウザータスクを開始しました。ブラウザー画面でライブ表示を確認できます。",
    ko: "브라우저 작업을 시작했습니다. 브라우저 화면에서 실시간으로 볼 수 있습니다.",
    ru: "Я запустил задачу в браузере. Вы можете наблюдать за ней в реальном времени.",
    ar: "بدأت مهمة المتصفح. يمكنك مشاهدتها مباشرة في عرض المتصفح.",
    hi: "मैंने ब्राउज़र कार्य शुरू कर दिया है। आप इसे ब्राउज़र दृश्य में लाइव देख सकते हैं।",
  },
  browser_needs_input: {
    en: "I've opened a browser session for this task, but I need some additional input from you to continue. Please check the browser view.",
    es: "He abierto una sesión del navegador para esta tarea, pero necesito información adicional tuya para continuar. Revisa la vista del navegador.",
    fr: "J'ai ouvert une session de navigateur pour cette tâche, mais j'ai besoin d'informations supplémentaires de votre part pour continuer. Veuillez vérifier la vue du navigateur.",
    de: "Ich habe für diese Aufgabe eine Browsersitzung geöffnet, brauche aber zusätzliche Angaben von Ihnen, um fortzufahren. Bitte prüfen Sie die Browseransicht.",
    it: "Ho aperto una sessione del browser per questa attività, ma ho bisogno di qualche informazione in più da parte tua per continuare. Controlla la vista del browser.",
    pt: "Abri uma sessão do navegador para esta tarefa, mas preciso de mais informações suas para continuar. Verifique a visualização do navegador.",
    zh: "我已经为这项任务打开了浏览器会话，但还需要你提供一些额外信息才能继续。请查看浏览器视图。",
    ja: "このタスクのためにブラウザーセッションを開きましたが、続行するには追加の入力が必要です。ブラウザー画面を確認してください。",
    ko: "이 작업을 위해 브라우저 세션을 열었지만 계속하려면 추가 입력이 필요합니다. 브라우저 화면을 확인해 주세요.",
    ru: "Я открыл браузерную сессию для этой задачи, но мне нужна дополнительная информация от вас, чтобы продолжить. Проверьте окно браузера.",
    ar: "فتحت جلسة متصفح لهذه المهمة، لكنني أحتاج إلى بعض المعلومات الإضافية منك للمتابعة. يرجى التحقق من عرض المتصفح.",
    hi: "मैंने इस कार्य के लिए ब्राउज़र सत्र खोल दिया है, लेकिन आगे बढ़ने के लिए मुझे आपसे कुछ अतिरिक्त जानकारी चाहिए। कृपया ब्राउज़र दृश्य देखें।",
  },
  browser_input_prompt: {
    en: "The agent needs additional information to continue. Please interact with the browser or provide more details.",
    es: "El agente necesita información adicional para continuar. Interactúa con el navegador o proporciona más detalles.",
    fr: "L'agent a besoin d'informations supplémentaires pour continuer. Interagissez avec le navigateur ou fournissez plus de détails.",
    de: "Der Agent benötigt zusätzliche Informationen, um fortzufahren. Interagieren Sie mit dem Browser oder geben Sie weitere Details an.",
    it: "L'agente ha bisogno di informazioni aggiuntive per continuare. Interagisci con il browser o fornisci maggiori dettagli.",
    pt: "O agente precisa de informações adicionais para continuar. Interaja com o navegador ou forneça mais detalhes.",
    zh: "智能体需要更多信息才能继续。请与浏览器交互，或提供更多细节。",
    ja: "続行するには追加情報が必要です。ブラウザー上で操作するか、さらに詳しい情報を入力してください。",
    ko: "계속하려면 추가 정보가 필요합니다. 브라우저에서 직접 작업하거나 더 자세한 정보를 제공해 주세요.",
    ru: "Агенту нужна дополнительная информация для продолжения. Поработайте в браузере или дайте больше деталей.",
    ar: "يحتاج الوكيل إلى معلومات إضافية للمتابعة. يرجى التفاعل مع المتصفح أو تقديم مزيد من التفاصيل.",
    hi: "आगे बढ़ने के लिए एजेंट को अतिरिक्त जानकारी चाहिए। कृपया ब्राउज़र के साथ इंटरैक्ट करें या अधिक विवरण दें।",
  },
  assisted_browse_started: {
    en: "I'll stop for your go-ahead before anything is booked or paid.",
    es: "Me detendré para que lo apruebes antes de reservar o pagar nada.",
    fr: "Je m'arrêterai pour votre accord avant toute réservation ou tout paiement.",
    de: "Vor jeder Buchung oder Zahlung halte ich für Ihre Freigabe an.",
    it: "Mi fermerò per il tuo via libera prima di prenotare o pagare qualsiasi cosa.",
    pt: "Vou parar para sua aprovação antes de reservar ou pagar qualquer coisa.",
    zh: "在预订或付款之前，我会停下来等你确认。",
    ja: "予約や支払いの前に、必ずあなたの承認を待ちます。",
    ko: "예약하거나 결제하기 전에 꼭 승인을 기다릴게요.",
    ru: "Перед любым бронированием или оплатой я остановлюсь и дождусь вашего согласия.",
    ar: "سأتوقف لأخذ موافقتك قبل أي حجز أو دفع.",
    hi: "कुछ भी बुक या भुगतान करने से पहले मैं आपकी मंज़ूरी के लिए रुकूँगा।",
  },
  assisted_browse_needs_go_ahead: {
    en: "I need your go-ahead before I start this on the site.",
    es: "Necesito tu aprobación antes de empezar en el sitio.",
    fr: "J'ai besoin de votre accord avant de commencer sur le site.",
    de: "Ich brauche Ihre Freigabe, bevor ich auf der Website beginne.",
    it: "Mi serve il tuo via libera prima di iniziare sul sito.",
    pt: "Preciso da sua aprovação antes de começar no site.",
    zh: "在网站上开始之前，我需要你的确认。",
    ja: "サイトで始める前に、あなたの承認が必要です。",
    ko: "사이트에서 시작하기 전에 승인이 필요해요.",
    ru: "Мне нужно ваше согласие, прежде чем начать на сайте.",
    ar: "أحتاج إلى موافقتك قبل أن أبدأ على الموقع.",
    hi: "साइट पर शुरू करने से पहले मुझे आपकी मंज़ूरी चाहिए।",
  },
  assisted_browse_confirm_on_card: {
    en: "Confirm on the card in the browser view; I can't confirm that from chat.",
    es: "Confírmalo en la tarjeta de la vista del navegador; no puedo hacerlo desde el chat.",
    fr: "Confirmez sur la carte de la vue du navigateur ; je ne peux pas le faire depuis le chat.",
    de: "Bestätigen Sie auf der Karte in der Browseransicht; aus dem Chat kann ich das nicht.",
    it: "Conferma sulla scheda nella vista del browser; non posso farlo dalla chat.",
    pt: "Confirme no cartão da visualização do navegador; não consigo fazer isso pelo chat.",
    zh: "请在浏览器视图的卡片上确认；我无法在聊天中确认。",
    ja: "ブラウザー画面のカードで確定してください。チャットからは確定できません。",
    ko: "브라우저 화면의 카드에서 확정해 주세요. 채팅에서는 확정할 수 없어요.",
    ru: "Подтвердите на карточке в окне браузера; из чата я этого сделать не могу.",
    ar: "أكّد من البطاقة في عرض المتصفح؛ لا يمكنني التأكيد من الدردشة.",
    hi: "ब्राउज़र दृश्य के कार्ड पर पुष्टि करें; मैं चैट से इसकी पुष्टि नहीं कर सकता।",
  },
  assisted_browse_site_required: {
    en: "Which site should I use for this?",
    es: "¿Qué sitio debo usar para esto?",
    fr: "Quel site dois-je utiliser pour cela ?",
    de: "Welche Website soll ich dafür verwenden?",
    it: "Quale sito devo usare per questo?",
    pt: "Qual site devo usar para isso?",
    zh: "我应该用哪个网站来办这件事？",
    ja: "どのサイトを使えばよいですか。",
    ko: "어느 사이트를 사용할까요?",
    ru: "Какой сайт мне для этого использовать?",
    ar: "أي موقع يجب أن أستخدم لهذا؟",
    hi: "इसके लिए मुझे कौन-सी साइट इस्तेमाल करनी चाहिए?",
  },
  browser_failed_with_detail: {
    en: "I encountered an error while browsing: {error}",
    es: "Encontré un error mientras navegaba: {error}",
    fr: "J'ai rencontré une erreur pendant la navigation : {error}",
    de: "Beim Browsen ist ein Fehler aufgetreten: {error}",
    it: "Ho riscontrato un errore durante la navigazione: {error}",
    pt: "Encontrei um erro durante a navegação: {error}",
    zh: "浏览时遇到了错误：{error}",
    ja: "ブラウジング中にエラーが発生しました: {error}",
    ko: "브라우징 중 오류가 발생했습니다: {error}",
    ru: "Во время работы в браузере произошла ошибка: {error}",
    ar: "واجهت خطأ أثناء التصفح: {error}",
    hi: "ब्राउज़िंग के दौरान एक त्रुटि हुई: {error}",
  },
  browser_task_success: {
    en: "I've completed the browser task successfully.",
    es: "He completado la tarea del navegador correctamente.",
    fr: "J'ai terminé la tâche dans le navigateur avec succès.",
    de: "Ich habe die Browseraufgabe erfolgreich abgeschlossen.",
    it: "Ho completato con successo l'attività nel browser.",
    pt: "Concluí a tarefa no navegador com sucesso.",
    zh: "我已经成功完成了浏览器任务。",
    ja: "ブラウザーのタスクを正常に完了しました。",
    ko: "브라우저 작업을 성공적으로 완료했습니다.",
    ru: "Я успешно выполнил задачу в браузере.",
    ar: "أكملت مهمة المتصفح بنجاح.",
    hi: "मैंने ब्राउज़र कार्य सफलतापूर्वक पूरा कर लिया है।",
  },
  browser_generic_error: {
    en: "Sorry, I encountered an error while using the browser. Please try again.",
    es: "Lo siento, encontré un error al usar el navegador. Inténtalo de nuevo.",
    fr: "Désolé, j'ai rencontré une erreur en utilisant le navigateur. Veuillez réessayer.",
    de: "Entschuldigung, bei der Verwendung des Browsers ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Mi dispiace, si è verificato un errore durante l'uso del browser. Riprova.",
    pt: "Desculpe, encontrei um erro ao usar o navegador. Tente novamente.",
    zh: "抱歉，使用浏览器时出错了。请再试一次。",
    ja: "申し訳ありません。ブラウザーの使用中にエラーが発生しました。もう一度お試しください。",
    ko: "죄송합니다. 브라우저를 사용하는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "Извините, при использовании браузера произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "عذرًا، حدث خطأ أثناء استخدام المتصفح. يرجى المحاولة مرة أخرى.",
    hi: "माफ़ कीजिए, ब्राउज़र का उपयोग करते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  document_create_error: {
    en: "Sorry, I encountered an error while creating the document. Please try again.",
    es: "Lo siento, encontré un error al crear el documento. Inténtalo de nuevo.",
    fr: "Désolé, j'ai rencontré une erreur lors de la création du document. Veuillez réessayer.",
    de: "Entschuldigung, beim Erstellen des Dokuments ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Mi dispiace, si è verificato un errore durante la creazione del documento. Riprova.",
    pt: "Desculpe, encontrei um erro ao criar o documento. Tente novamente.",
    zh: "抱歉，创建文档时出错了。请再试一次。",
    ja: "申し訳ありません。ドキュメントの作成中にエラーが発生しました。もう一度お試しください。",
    ko: "죄송합니다. 문서를 만드는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "Извините, при создании документа произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "عذرًا، حدث خطأ أثناء إنشاء المستند. يرجى المحاولة مرة أخرى.",
    hi: "माफ़ कीजिए, दस्तावेज़ बनाते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  excel_data_structure_error: {
    en: "I had trouble understanding the data structure. Please provide more structured data or specify the fields you want in the Excel file.",
    es: "Tuve problemas para entender la estructura de los datos. Proporciona datos más estructurados o especifica los campos que quieres en el archivo de Excel.",
    fr: "J'ai eu du mal à comprendre la structure des données. Veuillez fournir des données plus structurées ou préciser les champs souhaités dans le fichier Excel.",
    de: "Ich hatte Schwierigkeiten, die Datenstruktur zu verstehen. Bitte geben Sie strukturiertere Daten an oder nennen Sie die Felder, die Sie in der Excel-Datei möchten.",
    it: "Ho avuto difficoltà a capire la struttura dei dati. Fornisci dati più strutturati o specifica i campi che vuoi nel file Excel.",
    pt: "Tive dificuldade para entender a estrutura dos dados. Forneça dados mais estruturados ou especifique os campos que você quer no arquivo do Excel.",
    zh: "我不太能理解数据结构。请提供更结构化的数据，或说明你希望 Excel 文件中包含哪些字段。",
    ja: "データ構造の理解に問題がありました。より構造化されたデータを渡すか、Excel ファイルに入れたい項目を指定してください。",
    ko: "데이터 구조를 이해하는 데 어려움이 있었습니다. 더 구조화된 데이터를 제공하거나 Excel 파일에 넣을 필드를 지정해 주세요.",
    ru: "Мне не удалось понять структуру данных. Предоставьте более структурированные данные или укажите поля, которые нужны в файле Excel.",
    ar: "واجهت صعوبة في فهم بنية البيانات. يرجى تقديم بيانات أكثر تنظيمًا أو تحديد الحقول التي تريدها في ملف Excel.",
    hi: "मुझे डेटा की संरचना समझने में कठिनाई हुई। कृपया अधिक संरचित डेटा दें या बताएं कि Excel फ़ाइल में कौन-से फ़ील्ड चाहिए।",
  },
  excel_config_error: {
    en: "I had trouble creating the Excel file configuration. Please try again with a simpler request.",
    es: "Tuve problemas para crear la configuración del archivo de Excel. Inténtalo de nuevo con una solicitud más simple.",
    fr: "J'ai eu du mal à créer la configuration du fichier Excel. Veuillez réessayer avec une demande plus simple.",
    de: "Ich hatte Probleme beim Erstellen der Excel-Dateikonfiguration. Bitte versuchen Sie es mit einer einfacheren Anfrage erneut.",
    it: "Ho avuto difficoltà a creare la configurazione del file Excel. Riprova con una richiesta più semplice.",
    pt: "Tive dificuldade para criar a configuração do arquivo do Excel. Tente novamente com uma solicitação mais simples.",
    zh: "创建 Excel 文件配置时遇到了问题。请用更简单的请求再试一次。",
    ja: "Excel ファイル設定の作成に問題がありました。もう少し簡単な依頼でもう一度試してください。",
    ko: "Excel 파일 구성을 만드는 데 문제가 있었습니다. 더 간단한 요청으로 다시 시도해 주세요.",
    ru: "У меня возникли сложности при создании конфигурации Excel-файла. Попробуйте еще раз с более простым запросом.",
    ar: "واجهت مشكلة في إنشاء إعدادات ملف Excel. يرجى المحاولة مرة أخرى بطلب أبسط.",
    hi: "Excel फ़ाइल कॉन्फ़िगरेशन बनाते समय मुझे दिक्कत हुई। कृपया एक सरल अनुरोध के साथ फिर से कोशिश करें।",
  },
  excel_created: {
    en: 'I\'ve created an Excel file "{filename}" with {rowCount} rows of data across {sheetCount} sheets. The file includes {columnCount} data columns based on your request. You can download it using the button below.',
    es: 'He creado un archivo de Excel "{filename}" con {rowCount} filas de datos en {sheetCount} hojas. El archivo incluye {columnCount} columnas de datos según tu solicitud. Puedes descargarlo con el botón de abajo.',
    fr: 'J\'ai créé un fichier Excel "{filename}" avec {rowCount} lignes de données sur {sheetCount} feuilles. Le fichier comprend {columnCount} colonnes de données selon votre demande. Vous pouvez le télécharger avec le bouton ci-dessous.',
    de: 'Ich habe eine Excel-Datei "{filename}" mit {rowCount} Datenzeilen auf {sheetCount} Tabellenblättern erstellt. Die Datei enthält {columnCount} Datenspalten entsprechend Ihrer Anfrage. Sie können sie mit der Schaltfläche unten herunterladen.',
    it: 'Ho creato un file Excel "{filename}" con {rowCount} righe di dati su {sheetCount} fogli. Il file include {columnCount} colonne di dati in base alla tua richiesta. Puoi scaricarlo con il pulsante qui sotto.',
    pt: 'Criei um arquivo Excel "{filename}" com {rowCount} linhas de dados em {sheetCount} planilhas. O arquivo inclui {columnCount} colunas de dados com base no seu pedido. Você pode baixá-lo usando o botão abaixo.',
    zh: '我已经创建了一个 Excel 文件“{filename}”，其中包含 {rowCount} 行数据，分布在 {sheetCount} 个工作表中。该文件根据你的请求包含 {columnCount} 个数据列。你可以使用下方按钮下载它。',
    ja: '"{filename}" という Excel ファイルを作成しました。{sheetCount} 枚のシートに {rowCount} 行のデータがあり、リクエストに基づく {columnCount} 列が含まれています。下のボタンからダウンロードできます。',
    ko: '"{filename}" Excel 파일을 만들었습니다. {sheetCount}개 시트에 {rowCount}개의 데이터 행이 있으며, 요청에 따라 {columnCount}개의 데이터 열이 포함되어 있습니다. 아래 버튼으로 다운로드할 수 있습니다.',
    ru: 'Я создал файл Excel "{filename}" с {rowCount} строками данных на {sheetCount} листах. В файле {columnCount} столбцов данных в соответствии с вашим запросом. Вы можете скачать его кнопкой ниже.',
    ar: 'أنشأت ملف Excel باسم "{filename}" يحتوي على {rowCount} صفًا من البيانات عبر {sheetCount} أوراق. ويتضمن الملف {columnCount} أعمدة بيانات بناءً على طلبك. يمكنك تنزيله باستخدام الزر أدناه.',
    hi: 'मैंने "{filename}" नाम की एक Excel फ़ाइल बना दी है, जिसमें {sheetCount} शीट्स में {rowCount} डेटा पंक्तियाँ हैं। आपकी अनुरोध के आधार पर इसमें {columnCount} डेटा कॉलम शामिल हैं। आप इसे नीचे दिए गए बटन से डाउनलोड कर सकते हैं।',
  },
  document_plan_created: {
    en: 'I\'ve created a {fileTypeLabel} plan titled "{title}" with {pageCount} {pageLabel}. I\'m now generating each {pageUnit} directly in your viewer.',
    es: 'He creado un plan de {fileTypeLabel} titulado "{title}" con {pageCount} {pageLabel}. Ahora estoy generando cada {pageUnit} directamente en tu visor.',
    fr: 'J\'ai créé un plan de {fileTypeLabel} intitulé "{title}" avec {pageCount} {pageLabel}. Je génère maintenant chaque {pageUnit} directement dans votre visionneuse.',
    de: 'Ich habe einen {fileTypeLabel}-Plan mit dem Titel "{title}" und {pageCount} {pageLabel} erstellt. Ich generiere jetzt jede {pageUnit} direkt in Ihrer Ansicht.',
    it: 'Ho creato un piano {fileTypeLabel} intitolato "{title}" con {pageCount} {pageLabel}. Ora sto generando ogni {pageUnit} direttamente nel tuo visualizzatore.',
    pt: 'Criei um plano de {fileTypeLabel} chamado "{title}" com {pageCount} {pageLabel}. Agora estou gerando cada {pageUnit} diretamente no seu visualizador.',
    zh: '我已经创建了一个名为“{title}”的 {fileTypeLabel} 方案，共有 {pageCount} 个{pageLabel}。现在我会直接在查看器中生成每个{pageUnit}。',
    ja: '"{title}" という {fileTypeLabel} プランを作成しました。{pageCount} {pageLabel}あり、これから各{pageUnit}をビューアー内で直接生成します。',
    ko: '"{title}"라는 {fileTypeLabel} 계획을 만들었습니다. 총 {pageCount}개의 {pageLabel}가 있으며, 이제 각 {pageUnit}을(를) 뷰어에서 바로 생성합니다.',
    ru: 'Я создал план {fileTypeLabel} под названием "{title}" с {pageCount} {pageLabel}. Сейчас я генерирую каждый {pageUnit} прямо в вашем просмотрщике.',
    ar: 'أنشأت خطة {fileTypeLabel} بعنوان "{title}" تحتوي على {pageCount} {pageLabel}. وأعمل الآن على إنشاء كل {pageUnit} مباشرة في العارض لديك.',
    hi: 'मैंने "{title}" शीर्षक वाली {fileTypeLabel} योजना बना दी है, जिसमें {pageCount} {pageLabel} हैं। अब मैं हर {pageUnit} को सीधे आपके व्यूअर में जनरेट कर रहा हूँ।',
  },
  cannot_edit_email_here: {
    en: "I can't edit emails using this function. For email editing, use ui_component_operations with an edit request, or draft a new email instead.",
    es: "No puedo editar correos con esta función. Para editar correos, usa ui_component_operations con una solicitud de edición o redacta un correo nuevo.",
    fr: "Je ne peux pas modifier les e-mails avec cette fonction. Pour modifier un e-mail, utilisez ui_component_operations avec une demande de modification, ou rédigez plutôt un nouvel e-mail.",
    de: "Ich kann E-Mails mit dieser Funktion nicht bearbeiten. Verwenden Sie für E-Mail-Bearbeitungen ui_component_operations mit einer Bearbeitungsanfrage oder erstellen Sie stattdessen eine neue E-Mail.",
    it: "Non posso modificare le email con questa funzione. Per modificare un'email, usa ui_component_operations con una richiesta di modifica oppure crea una nuova email.",
    pt: "Não consigo editar e-mails com esta função. Para editar e-mails, use ui_component_operations com um pedido de edição ou crie um novo e-mail.",
    zh: "我不能用这个函数编辑邮件。若要编辑邮件，请使用带编辑请求的 ui_component_operations，或者重新起草一封新邮件。",
    ja: "この機能ではメールを編集できません。メールを編集するには、編集リクエスト付きで ui_component_operations を使うか、新しいメールを下書きしてください。",
    ko: "이 기능으로는 이메일을 편집할 수 없습니다. 이메일을 편집하려면 편집 요청과 함께 ui_component_operations를 사용하거나 새 이메일 초안을 작성하세요.",
    ru: "Я не могу редактировать письма этой функцией. Для редактирования писем используйте ui_component_operations с запросом на изменение или создайте новое письмо.",
    ar: "لا يمكنني تعديل رسائل البريد الإلكتروني باستخدام هذه الوظيفة. لتعديل البريد الإلكتروني استخدم ui_component_operations مع طلب تعديل، أو أنشئ رسالة جديدة بدلًا من ذلك.",
    hi: "मैं इस फ़ंक्शन से ईमेल संपादित नहीं कर सकता। ईमेल संपादित करने के लिए edit request के साथ ui_component_operations का उपयोग करें, या नया ईमेल ड्राफ्ट करें।",
  },
  need_document_filename: {
    en: "I need the filename of the document you want to edit. Please specify which file you'd like me to modify.",
    es: "Necesito el nombre del archivo del documento que quieres editar. Indica qué archivo quieres que modifique.",
    fr: "J'ai besoin du nom du fichier du document que vous voulez modifier. Veuillez préciser quel fichier vous souhaitez que je modifie.",
    de: "Ich brauche den Dateinamen des Dokuments, das Sie bearbeiten möchten. Bitte geben Sie an, welche Datei ich ändern soll.",
    it: "Ho bisogno del nome del file del documento che vuoi modificare. Indica quale file vuoi che modifichi.",
    pt: "Preciso do nome do arquivo do documento que você quer editar. Informe qual arquivo você quer que eu modifique.",
    zh: "我需要你想编辑的文档文件名。请说明你希望我修改哪个文件。",
    ja: "編集したいドキュメントのファイル名が必要です。どのファイルを変更したいのか教えてください。",
    ko: "편집하려는 문서의 파일명이 필요합니다. 어떤 파일을 수정할지 알려 주세요.",
    ru: "Мне нужно имя файла документа, который вы хотите изменить. Укажите, какой именно файл нужно отредактировать.",
    ar: "أحتاج إلى اسم ملف المستند الذي تريد تعديله. يرجى تحديد الملف الذي تريدني أن أعدله.",
    hi: "जिस दस्तावेज़ को आप संपादित करना चाहते हैं उसका फ़ाइल नाम चाहिए। कृपया बताइए कि मैं किस फ़ाइल को बदलूँ।",
  },
  document_edit_target_ambiguous: {
    en: "Several documents are open ({candidates}). Tell me which one to edit and I'll change only that one.",
    es: "Hay varios documentos abiertos ({candidates}). Dime cuál quieres editar y modificaré solo ese.",
    fr: "Plusieurs documents sont ouverts ({candidates}). Dites-moi lequel modifier et je ne changerai que celui-là.",
    de: "Mehrere Dokumente sind geöffnet ({candidates}). Sagen Sie mir, welches ich bearbeiten soll, dann ändere ich nur dieses.",
    it: "Ci sono più documenti aperti ({candidates}). Dimmi quale modificare e cambierò solo quello.",
    pt: "Há vários documentos abertos ({candidates}). Diga qual deles editar e alterarei apenas esse.",
    zh: "当前打开了多个文档（{candidates}）。请告诉我要编辑哪一个，我只会修改那一个。",
    ja: "複数のドキュメントが開いています（{candidates}）。どれを編集するか教えてください。そのドキュメントだけを変更します。",
    ko: "여러 문서가 열려 있습니다({candidates}). 편집할 문서를 알려 주시면 그 문서만 수정하겠습니다.",
    ru: "Открыто несколько документов ({candidates}). Скажите, какой из них изменить, и я отредактирую только его.",
    ar: "هناك عدة مستندات مفتوحة ({candidates}). أخبرني أيها تريد تعديله وسأعدّل ذلك المستند فقط.",
    hi: "कई दस्तावेज़ खुले हैं ({candidates})। बताइए किसे संपादित करना है, मैं सिर्फ़ उसी को बदलूँगा।",
  },
  document_edit_identity_mismatch: {
    en: "The open document doesn't match the one this edit was meant for, so I didn't change anything. Please reopen the document and try again.",
    es: "El documento abierto no coincide con el que debía editarse, así que no cambié nada. Vuelve a abrir el documento e inténtalo de nuevo.",
    fr: "Le document ouvert ne correspond pas à celui visé par cette modification, je n'ai donc rien changé. Rouvrez le document et réessayez.",
    de: "Das geöffnete Dokument passt nicht zu dem, das bearbeitet werden sollte, daher habe ich nichts geändert. Bitte öffnen Sie das Dokument erneut und versuchen Sie es noch einmal.",
    it: "Il documento aperto non corrisponde a quello da modificare, quindi non ho cambiato nulla. Riapri il documento e riprova.",
    pt: "O documento aberto não corresponde ao que deveria ser editado, então não alterei nada. Reabra o documento e tente novamente.",
    zh: "当前打开的文档与这次编辑的目标不一致，所以我没有做任何修改。请重新打开该文档后再试。",
    ja: "開いているドキュメントが今回の編集対象と一致しないため、何も変更していません。ドキュメントを開き直してもう一度お試しください。",
    ko: "열려 있는 문서가 이번 편집 대상과 일치하지 않아 아무것도 변경하지 않았습니다. 문서를 다시 열고 다시 시도해 주세요.",
    ru: "Открытый документ не совпадает с тем, который нужно было изменить, поэтому я ничего не менял. Откройте документ заново и попробуйте ещё раз.",
    ar: "المستند المفتوح لا يطابق المستند المقصود بهذا التعديل، لذلك لم أغيّر شيئًا. يرجى إعادة فتح المستند والمحاولة مرة أخرى.",
    hi: "खुला दस्तावेज़ उस दस्तावेज़ से मेल नहीं खाता जिसे बदलना था, इसलिए मैंने कुछ नहीं बदला। कृपया दस्तावेज़ फिर से खोलें और दोबारा कोशिश करें।",
  },
  document_edit_source_incomplete: {
    en: "I couldn't load every page of {fileName} yet, so I didn't edit it to avoid losing content. Try again once it has finished loading.",
    es: "Todavía no pude cargar todas las páginas de {fileName}, así que no lo edité para no perder contenido. Inténtalo de nuevo cuando termine de cargarse.",
    fr: "Je n'ai pas encore pu charger toutes les pages de {fileName}, je ne l'ai donc pas modifié pour éviter toute perte de contenu. Réessayez une fois le chargement terminé.",
    de: "Ich konnte noch nicht alle Seiten von {fileName} laden und habe es daher nicht bearbeitet, damit kein Inhalt verloren geht. Versuchen Sie es erneut, sobald es vollständig geladen ist.",
    it: "Non sono ancora riuscito a caricare tutte le pagine di {fileName}, quindi non l'ho modificato per evitare di perdere contenuti. Riprova quando il caricamento sarà completato.",
    pt: "Ainda não consegui carregar todas as páginas de {fileName}, então não o editei para evitar perda de conteúdo. Tente novamente quando terminar de carregar.",
    zh: "我还无法加载 {fileName} 的全部页面，为避免丢失内容，我没有编辑它。请在加载完成后再试。",
    ja: "{fileName} のすべてのページをまだ読み込めていないため、内容が失われないよう編集していません。読み込みが完了してからもう一度お試しください。",
    ko: "{fileName}의 모든 페이지를 아직 불러오지 못해 내용 손실을 막기 위해 편집하지 않았습니다. 로딩이 끝난 후 다시 시도해 주세요.",
    ru: "Мне пока не удалось загрузить все страницы {fileName}, поэтому я не стал его менять, чтобы не потерять содержимое. Попробуйте снова после завершения загрузки.",
    ar: "لم أتمكن بعد من تحميل كل صفحات {fileName}، لذلك لم أعدّله لتجنّب فقدان المحتوى. حاول مرة أخرى بعد اكتمال التحميل.",
    hi: "मैं अभी {fileName} के सभी पेज लोड नहीं कर सका, इसलिए सामग्री खोने से बचाने के लिए मैंने इसे संपादित नहीं किया। लोड पूरा होने के बाद फिर कोशिश करें।",
  },
  document_edit_conflict: {
    en: "Another edit to {fileName} landed first, so I didn't apply this one to avoid overwriting it. Please re-apply your request on the latest version.",
    es: "Otra edición de {fileName} se guardó primero, así que no apliqué esta para no sobrescribirla. Vuelve a aplicar tu solicitud sobre la versión más reciente.",
    fr: "Une autre modification de {fileName} a été enregistrée en premier ; je n'ai donc pas appliqué celle-ci pour ne pas l'écraser. Veuillez refaire votre demande sur la dernière version.",
    de: "Eine andere Änderung an {fileName} wurde zuerst gespeichert, daher habe ich diese nicht angewendet, um sie nicht zu überschreiben. Bitte wenden Sie Ihre Anfrage erneut auf die neueste Version an.",
    it: "Un'altra modifica a {fileName} è stata salvata prima, quindi non ho applicato questa per non sovrascriverla. Riapplica la tua richiesta sull'ultima versione.",
    pt: "Outra edição de {fileName} foi salva primeiro, então não apliquei esta para não sobrescrevê-la. Aplique sua solicitação novamente na versão mais recente.",
    zh: "{fileName} 的另一处编辑已先保存，为避免覆盖它，我没有应用这次编辑。请在最新版本上重新提出你的请求。",
    ja: "{fileName} への別の編集が先に保存されたため、上書きしないよう今回の編集は適用していません。最新バージョンに対してもう一度リクエストしてください。",
    ko: "{fileName}에 대한 다른 편집이 먼저 저장되어, 덮어쓰지 않도록 이번 편집은 적용하지 않았습니다. 최신 버전에서 요청을 다시 적용해 주세요.",
    ru: "Другое изменение {fileName} было сохранено раньше, поэтому я не применил это, чтобы не перезаписать его. Пожалуйста, повторите запрос для последней версии.",
    ar: "تم حفظ تعديل آخر على {fileName} أولًا، لذلك لم أطبّق هذا التعديل حتى لا أستبدله. يرجى إعادة تطبيق طلبك على أحدث إصدار.",
    hi: "{fileName} में एक दूसरा संपादन पहले सहेजा गया, इसलिए उसे अधिलेखित होने से बचाने के लिए मैंने यह संपादन लागू नहीं किया। कृपया नवीनतम संस्करण पर अपना अनुरोध फिर से लागू करें।",
  },
  file_type_not_editable: {
    en: "I can't edit .{fileExtension} files directly. Supported formats are: PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX, and PPT.",
    es: "No puedo editar archivos .{fileExtension} directamente. Los formatos compatibles son: PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX y PPT.",
    fr: "Je ne peux pas modifier directement les fichiers .{fileExtension}. Les formats pris en charge sont : PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX et PPT.",
    de: "Ich kann Dateien vom Typ .{fileExtension} nicht direkt bearbeiten. Unterstützte Formate sind: PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX und PPT.",
    it: "Non posso modificare direttamente i file .{fileExtension}. I formati supportati sono: PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX e PPT.",
    pt: "Não consigo editar arquivos .{fileExtension} diretamente. Os formatos compatíveis são: PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX e PPT.",
    zh: "我不能直接编辑 .{fileExtension} 文件。支持的格式有：PDF、DOCX、DOC、TXT、HTML、RTF、ODT、MD、XLSX、XLS、PPTX 和 PPT。",
    ja: ".{fileExtension} ファイルは直接編集できません。対応形式は PDF、DOCX、DOC、TXT、HTML、RTF、ODT、MD、XLSX、XLS、PPTX、PPT です。",
    ko: ".{fileExtension} 파일은 직접 편집할 수 없습니다. 지원 형식은 PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX, PPT입니다.",
    ru: "Я не могу напрямую редактировать файлы .{fileExtension}. Поддерживаемые форматы: PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX и PPT.",
    ar: "لا يمكنني تعديل ملفات .{fileExtension} مباشرةً. التنسيقات المدعومة هي: PDF وDOCX وDOC وTXT وHTML وRTF وODT وMD وXLSX وXLS وPPTX وPPT.",
    hi: "मैं .{fileExtension} फ़ाइलों को सीधे संपादित नहीं कर सकता। समर्थित फ़ॉर्मैट हैं: PDF, DOCX, DOC, TXT, HTML, RTF, ODT, MD, XLSX, XLS, PPTX और PPT।",
  },
  slide_content_not_loaded: {
    en: "I couldn't load the slide content for that presentation yet. Open the slide in the viewer first, or regenerate it, and then ask me to edit it again.",
    es: "Todavía no pude cargar el contenido de esa diapositiva. Abre primero la diapositiva en el visor, o vuelve a generarla, y luego pídeme editarla otra vez.",
    fr: "Je n'ai pas encore pu charger le contenu de cette diapositive. Ouvrez d'abord la diapositive dans la visionneuse, ou régénérez-la, puis demandez-moi de la modifier de nouveau.",
    de: "Ich konnte den Folieninhalt dieser Präsentation noch nicht laden. Öffnen Sie die Folie zuerst im Viewer oder generieren Sie sie neu und bitten Sie mich dann erneut um die Bearbeitung.",
    it: "Non sono ancora riuscito a caricare il contenuto di quella diapositiva. Apri prima la diapositiva nel visualizzatore, oppure rigenerala, poi chiedimi di modificarla di nuovo.",
    pt: "Ainda não consegui carregar o conteúdo desse slide. Abra o slide no visualizador primeiro, ou gere-o novamente, e depois me peça para editá-lo de novo.",
    zh: "我暂时还无法加载那份演示文稿的幻灯片内容。请先在查看器中打开该幻灯片，或重新生成它，然后再让我编辑。",
    ja: "そのプレゼンテーションのスライド内容をまだ読み込めませんでした。先にビューアーでスライドを開くか再生成してから、もう一度編集を依頼してください。",
    ko: "해당 프레젠테이션의 슬라이드 내용을 아직 불러오지 못했습니다. 먼저 뷰어에서 슬라이드를 열거나 다시 생성한 뒤 다시 편집을 요청해 주세요.",
    ru: "Мне пока не удалось загрузить содержимое этого слайда. Сначала откройте слайд в просмотрщике или сгенерируйте его заново, а затем снова попросите меня внести изменения.",
    ar: "لم أتمكن بعد من تحميل محتوى الشريحة لهذا العرض. افتح الشريحة أولًا في العارض أو أعد إنشاءها، ثم اطلب مني تعديلها مرة أخرى.",
    hi: "मैं उस प्रेज़ेंटेशन की स्लाइड सामग्री अभी लोड नहीं कर पाया। पहले स्लाइड को व्यूअर में खोलें या उसे फिर से जनरेट करें, फिर मुझसे दोबारा संपादित करने को कहें।",
  },
  document_content_not_loaded: {
    en: "I couldn't find any content in the document to edit. Please make sure the document is loaded first by querying it from the database.",
    es: "No pude encontrar contenido en el documento para editar. Asegúrate primero de que el documento esté cargado consultándolo desde la base de datos.",
    fr: "Je n'ai trouvé aucun contenu à modifier dans le document. Assurez-vous d'abord que le document est chargé en le récupérant depuis la base de données.",
    de: "Ich konnte keinen bearbeitbaren Inhalt im Dokument finden. Stellen Sie bitte zuerst sicher, dass das Dokument aus der Datenbank geladen wurde.",
    it: "Non sono riuscito a trovare contenuto nel documento da modificare. Assicurati prima che il documento sia caricato recuperandolo dal database.",
    pt: "Não consegui encontrar conteúdo no documento para editar. Certifique-se primeiro de que o documento foi carregado consultando-o no banco de dados.",
    zh: "我在文档中没有找到可编辑的内容。请先确保通过数据库查询把文档加载出来。",
    ja: "編集できる内容がドキュメント内に見つかりませんでした。まずデータベースから取得してドキュメントを読み込んでください。",
    ko: "문서에서 편집할 내용을 찾지 못했습니다. 먼저 데이터베이스에서 문서를 조회해 불러와 주세요.",
    ru: "Я не нашел в документе содержимого для редактирования. Сначала убедитесь, что документ загружен из базы данных.",
    ar: "لم أجد أي محتوى في المستند لتعديله. يرجى التأكد أولًا من تحميل المستند عن طريق استرجاعه من قاعدة البيانات.",
    hi: "मुझे दस्तावेज़ में संपादित करने के लिए कोई सामग्री नहीं मिली। कृपया पहले डेटाबेस से दस्तावेज़ को लोड कर लें।",
  },
  no_editable_pages_updated: {
    en: "I couldn't apply the requested edits because no slide content was updated. Please try again with a more specific instruction or reload the presentation.",
    es: "No pude aplicar las ediciones solicitadas porque no se actualizó el contenido de ninguna diapositiva. Inténtalo de nuevo con una instrucción más específica o vuelve a cargar la presentación.",
    fr: "Je n'ai pas pu appliquer les modifications demandées parce qu'aucun contenu de diapositive n'a été mis à jour. Veuillez réessayer avec une instruction plus précise ou recharger la présentation.",
    de: "Ich konnte die gewünschten Änderungen nicht anwenden, weil kein Folieninhalt aktualisiert wurde. Bitte versuchen Sie es mit einer genaueren Anweisung erneut oder laden Sie die Präsentation neu.",
    it: "Non sono riuscito ad applicare le modifiche richieste perché il contenuto di nessuna diapositiva è stato aggiornato. Riprova con un'istruzione più specifica oppure ricarica la presentazione.",
    pt: "Não consegui aplicar as edições solicitadas porque nenhum conteúdo de slide foi atualizado. Tente novamente com uma instrução mais específica ou recarregue a apresentação.",
    zh: "我没能应用这些编辑请求，因为没有任何幻灯片内容被更新。请用更具体的说明再试一次，或者重新加载演示文稿。",
    ja: "更新されたスライド内容がなかったため、依頼された編集を適用できませんでした。もう少し具体的な指示で再試行するか、プレゼンテーションを再読み込みしてください。",
    ko: "업데이트된 슬라이드 내용이 없어 요청한 편집을 적용할 수 없었습니다. 더 구체적인 지시로 다시 시도하거나 프레젠테이션을 다시 불러와 주세요.",
    ru: "Мне не удалось применить запрошенные изменения, потому что содержимое слайдов не обновилось. Попробуйте еще раз с более конкретной инструкцией или перезагрузите презентацию.",
    ar: "لم أتمكن من تطبيق التعديلات المطلوبة لأنه لم يتم تحديث محتوى أي شريحة. يرجى المحاولة مرة أخرى بتعليمات أكثر تحديدًا أو إعادة تحميل العرض.",
    hi: "मैं अनुरोधित संपादन लागू नहीं कर पाया क्योंकि किसी भी स्लाइड की सामग्री अपडेट नहीं हुई। कृपया अधिक स्पष्ट निर्देश के साथ फिर से कोशिश करें या प्रेज़ेंटेशन को दोबारा लोड करें।",
  },
  document_updated_current_viewer: {
    en: 'I\'ve updated "{fileName}" in the current viewer. {summary}.',
    es: 'He actualizado "{fileName}" en el visor actual. {summary}.',
    fr: 'J\'ai mis à jour "{fileName}" dans la visionneuse actuelle. {summary}.',
    de: 'Ich habe "{fileName}" in der aktuellen Ansicht aktualisiert. {summary}.',
    it: 'Ho aggiornato "{fileName}" nel visualizzatore attuale. {summary}.',
    pt: 'Atualizei "{fileName}" no visualizador atual. {summary}.',
    zh: '我已经在当前查看器中更新了“{fileName}”。{summary}。',
    ja: '現在のビューアーで「{fileName}」を更新しました。{summary}。',
    ko: '현재 뷰어에서 "{fileName}"을(를) 업데이트했습니다. {summary}.',
    ru: 'Я обновил "{fileName}" в текущем просмотрщике. {summary}.',
    ar: 'لقد حدّثت "{fileName}" في العارض الحالي. {summary}.',
    hi: 'मैंने वर्तमान व्यूअर में "{fileName}" अपडेट कर दिया है। {summary}।',
  },
  document_edited_displayed: {
    en: 'I\'ve edited "{fileName}". {summary}. The updated document is now displayed.',
    es: 'He editado "{fileName}". {summary}. El documento actualizado ya se muestra.',
    fr: 'J\'ai modifié "{fileName}". {summary}. Le document mis à jour est maintenant affiché.',
    de: 'Ich habe "{fileName}" bearbeitet. {summary}. Das aktualisierte Dokument wird jetzt angezeigt.',
    it: 'Ho modificato "{fileName}". {summary}. Il documento aggiornato è ora visualizzato.',
    pt: 'Editei "{fileName}". {summary}. O documento atualizado agora está sendo exibido.',
    zh: '我已经编辑了“{fileName}”。{summary}。更新后的文档现在已经显示出来。',
    ja: '「{fileName}」を編集しました。{summary}。更新後のドキュメントが表示されています。',
    ko: '"{fileName}"을(를) 편집했습니다. {summary}. 업데이트된 문서가 이제 표시되고 있습니다.',
    ru: 'Я отредактировал "{fileName}". {summary}. Обновленный документ уже отображается.',
    ar: 'لقد عدّلت "{fileName}". {summary}. والمستند المحدّث معروض الآن.',
    hi: 'मैंने "{fileName}" को संपादित कर दिया है। {summary}। अपडेट किया गया दस्तावेज़ अब दिख रहा है।',
  },
  document_edit_error: {
    en: "Sorry, I encountered an error while editing the document. Please try again.",
    es: "Lo siento, encontré un error al editar el documento. Inténtalo de nuevo.",
    fr: "Désolé, j'ai rencontré une erreur lors de la modification du document. Veuillez réessayer.",
    de: "Entschuldigung, beim Bearbeiten des Dokuments ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Mi dispiace, si è verificato un errore durante la modifica del documento. Riprova.",
    pt: "Desculpe, encontrei um erro ao editar o documento. Tente novamente.",
    zh: "抱歉，编辑文档时出错了。请再试一次。",
    ja: "申し訳ありません。ドキュメントの編集中にエラーが発生しました。もう一度お試しください。",
    ko: "죄송합니다. 문서를 편집하는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "Извините, при редактировании документа произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "عذرًا، حدث خطأ أثناء تعديل المستند. يرجى المحاولة مرة أخرى.",
    hi: "माफ़ कीजिए, दस्तावेज़ संपादित करते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  convert_need_filename: {
    en: "I need the filename to convert. Please upload the file you want to convert.",
    es: "Necesito el nombre del archivo para convertirlo. Sube el archivo que quieres convertir.",
    fr: "J'ai besoin du nom du fichier à convertir. Veuillez téléverser le fichier que vous voulez convertir.",
    de: "Ich brauche den Dateinamen für die Konvertierung. Bitte laden Sie die Datei hoch, die Sie konvertieren möchten.",
    it: "Ho bisogno del nome del file da convertire. Carica il file che vuoi convertire.",
    pt: "Preciso do nome do arquivo para converter. Envie o arquivo que você quer converter.",
    zh: "我需要要转换的文件名。请上传你想转换的文件。",
    ja: "変換するファイル名が必要です。変換したいファイルをアップロードしてください。",
    ko: "변환할 파일명이 필요합니다. 변환하려는 파일을 업로드해 주세요.",
    ru: "Мне нужно имя файла для конвертации. Загрузите файл, который хотите преобразовать.",
    ar: "أحتاج إلى اسم الملف لتحويله. يرجى رفع الملف الذي تريد تحويله.",
    hi: "कन्वर्ट करने के लिए मुझे फ़ाइल का नाम चाहिए। कृपया वह फ़ाइल अपलोड करें जिसे आप कन्वर्ट करना चाहते हैं।",
  },
  convert_need_output_format: {
    en: 'What format would you like to convert "{fileName}" to?',
    es: '¿A qué formato quieres convertir "{fileName}"?',
    fr: 'Vers quel format souhaitez-vous convertir "{fileName}" ?',
    de: 'In welches Format möchten Sie "{fileName}" konvertieren?',
    it: 'In quale formato vuoi convertire "{fileName}"?',
    pt: 'Para qual formato você quer converter "{fileName}"?',
    zh: '你想把“{fileName}”转换成什么格式？',
    ja: '「{fileName}」をどの形式に変換したいですか。',
    ko: '"{fileName}"을(를) 어떤 형식으로 변환하시겠습니까?',
    ru: 'В какой формат вы хотите преобразовать "{fileName}"?',
    ar: 'إلى أي تنسيق تريد تحويل "{fileName}"؟',
    hi: '"{fileName}" को आप किस फ़ॉर्मैट में कन्वर्ट करना चाहते हैं?',
  },
  convert_already_format: {
    en: 'The file "{fileName}" is already in {outputFormat} format.',
    es: 'El archivo "{fileName}" ya está en formato {outputFormat}.',
    fr: 'Le fichier "{fileName}" est déjà au format {outputFormat}.',
    de: 'Die Datei "{fileName}" liegt bereits im Format {outputFormat} vor.',
    it: 'Il file "{fileName}" è già nel formato {outputFormat}.',
    pt: 'O arquivo "{fileName}" já está no formato {outputFormat}.',
    zh: '文件“{fileName}”已经是 {outputFormat} 格式。',
    ja: 'ファイル「{fileName}」はすでに {outputFormat} 形式です。',
    ko: '"{fileName}" 파일은 이미 {outputFormat} 형식입니다.',
    ru: 'Файл "{fileName}" уже находится в формате {outputFormat}.',
    ar: 'الملف "{fileName}" موجود بالفعل بتنسيق {outputFormat}.',
    hi: 'फ़ाइल "{fileName}" पहले से ही {outputFormat} फ़ॉर्मैट में है।',
  },
  convert_success: {
    en: 'I\'ve converted "{fileName}" to {outputFormat} format. You can download it using the link below.',
    es: 'He convertido "{fileName}" al formato {outputFormat}. Puedes descargarlo con el enlace de abajo.',
    fr: 'J\'ai converti "{fileName}" au format {outputFormat}. Vous pouvez le télécharger avec le lien ci-dessous.',
    de: 'Ich habe "{fileName}" in das Format {outputFormat} konvertiert. Sie können die Datei über den Link unten herunterladen.',
    it: 'Ho convertito "{fileName}" nel formato {outputFormat}. Puoi scaricarlo con il link qui sotto.',
    pt: 'Convertemos "{fileName}" para o formato {outputFormat}. Você pode baixá-lo pelo link abaixo.',
    zh: '我已经把“{fileName}”转换成 {outputFormat} 格式。你可以使用下方链接下载。',
    ja: '「{fileName}」を {outputFormat} 形式に変換しました。下のリンクからダウンロードできます。',
    ko: '"{fileName}"을(를) {outputFormat} 형식으로 변환했습니다. 아래 링크에서 다운로드할 수 있습니다.',
    ru: 'Я преобразовал "{fileName}" в формат {outputFormat}. Вы можете скачать его по ссылке ниже.',
    ar: 'لقد حوّلت "{fileName}" إلى تنسيق {outputFormat}. يمكنك تنزيله باستخدام الرابط أدناه.',
    hi: 'मैंने "{fileName}" को {outputFormat} फ़ॉर्मैट में कन्वर्ट कर दिया है। आप इसे नीचे दिए गए लिंक से डाउनलोड कर सकते हैं।',
  },
  convert_unsupported: {
    en: "Sorry, I can't convert from {inputFormat} to {outputFormat}.",
    es: "Lo siento, no puedo convertir de {inputFormat} a {outputFormat}.",
    fr: "Désolé, je ne peux pas convertir de {inputFormat} vers {outputFormat}.",
    de: "Entschuldigung, ich kann nicht von {inputFormat} nach {outputFormat} konvertieren.",
    it: "Mi dispiace, non posso convertire da {inputFormat} a {outputFormat}.",
    pt: "Desculpe, não consigo converter de {inputFormat} para {outputFormat}.",
    zh: "抱歉，我不能把 {inputFormat} 转换成 {outputFormat}。",
    ja: "申し訳ありません。{inputFormat} から {outputFormat} への変換には対応していません。",
    ko: "죄송합니다. {inputFormat}에서 {outputFormat}(으)로는 변환할 수 없습니다.",
    ru: "Извините, я не могу преобразовать {inputFormat} в {outputFormat}.",
    ar: "عذرًا، لا يمكنني التحويل من {inputFormat} إلى {outputFormat}.",
    hi: "माफ़ कीजिए, मैं {inputFormat} से {outputFormat} में कन्वर्ट नहीं कर सकता।",
  },
  convert_error: {
    en: "Sorry, I encountered an error while converting the file. Please try again.",
    es: "Lo siento, encontré un error al convertir el archivo. Inténtalo de nuevo.",
    fr: "Désolé, j'ai rencontré une erreur lors de la conversion du fichier. Veuillez réessayer.",
    de: "Entschuldigung, beim Konvertieren der Datei ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Mi dispiace, si è verificato un errore durante la conversione del file. Riprova.",
    pt: "Desculpe, encontrei um erro ao converter o arquivo. Tente novamente.",
    zh: "抱歉，转换文件时出错了。请再试一次。",
    ja: "申し訳ありません。ファイル変換中にエラーが発生しました。もう一度お試しください。",
    ko: "죄송합니다. 파일을 변환하는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "Извините, при конвертации файла произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "عذرًا، حدث خطأ أثناء تحويل الملف. يرجى المحاولة مرة أخرى.",
    hi: "माफ़ कीजिए, फ़ाइल कन्वर्ट करते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  need_more_source_information: {
    en: "I need more source information before creating this document.{missingHints} Please provide details or allow me to search your data or the web.",
    es: "Necesito más información de origen antes de crear este documento.{missingHints} Proporciona más detalles o permíteme buscar en tus datos o en la web.",
    fr: "J'ai besoin de plus d'informations sources avant de créer ce document.{missingHints} Veuillez fournir plus de détails ou m'autoriser à rechercher dans vos données ou sur le web.",
    de: "Ich brauche mehr Quellinformationen, bevor ich dieses Dokument erstellen kann.{missingHints} Bitte geben Sie mehr Details an oder erlauben Sie mir, Ihre Daten oder das Web zu durchsuchen.",
    it: "Ho bisogno di più informazioni di origine prima di creare questo documento.{missingHints} Fornisci più dettagli oppure consentimi di cercare nei tuoi dati o sul web.",
    pt: "Preciso de mais informações de origem antes de criar este documento.{missingHints} Forneça mais detalhes ou me permita pesquisar nos seus dados ou na web.",
    zh: "在创建这份文档之前，我还需要更多源信息。{missingHints}请提供更多细节，或者允许我搜索你的数据或网络。",
    ja: "このドキュメントを作成する前に、もう少し元情報が必要です。{missingHints}詳細を教えるか、あなたのデータやウェブを検索できるようにしてください。",
    ko: "이 문서를 만들기 전에 추가 소스 정보가 더 필요합니다.{missingHints} 자세한 내용을 알려 주시거나, 데이터나 웹을 검색할 수 있게 해 주세요.",
    ru: "Мне нужно больше исходной информации, прежде чем создавать этот документ.{missingHints} Уточните детали или разрешите мне поискать в ваших данных или в интернете.",
    ar: "أحتاج إلى مزيد من معلومات المصدر قبل إنشاء هذا المستند.{missingHints} يرجى تقديم تفاصيل إضافية أو السماح لي بالبحث في بياناتك أو على الويب.",
    hi: "इस दस्तावेज़ को बनाने से पहले मुझे और स्रोत जानकारी चाहिए।{missingHints} कृपया अधिक विवरण दें या मुझे आपके डेटा या वेब में खोजने दें।",
  },
  document_operation_prompt_required: {
    en: "Tell me what document you want to create, edit, or convert.",
    es: "Dime qué documento quieres crear, editar o convertir.",
    fr: "Dites-moi quel document vous voulez créer, modifier ou convertir.",
    de: "Sagen Sie mir, welches Dokument Sie erstellen, bearbeiten oder konvertieren möchten.",
    it: "Dimmi quale documento vuoi creare, modificare o convertire.",
    pt: "Diga qual documento você quer criar, editar ou converter.",
    zh: "请告诉我你想创建、编辑或转换哪个文档。",
    ja: "作成、編集、または変換したいドキュメントを教えてください。",
    ko: "생성, 편집 또는 변환하려는 문서를 알려 주세요.",
    ru: "Скажите, какой документ вы хотите создать, изменить или преобразовать.",
    ar: "أخبرني بأي مستند تريد إنشاءه أو تعديله أو تحويله.",
    hi: "मुझे बताइए कि आप कौन-सा दस्तावेज़ बनाना, संपादित करना या कन्वर्ट करना चाहते हैं।",
  },
  search_results_fallback: {
    en: "Search results for: {query}",
    es: "Resultados de búsqueda para: {query}",
    fr: "Résultats de recherche pour : {query}",
    de: "Suchergebnisse für: {query}",
    it: "Risultati della ricerca per: {query}",
    pt: "Resultados da pesquisa para: {query}",
    zh: "“{query}”的搜索结果：",
    ja: "「{query}」の検索結果:",
    ko: "{query}에 대한 검색 결과:",
    ru: "Результаты поиска по запросу: {query}",
    ar: "نتائج البحث عن: {query}",
    hi: "{query} के लिए खोज परिणाम:",
  },
  search_title: {
    en: "Search: {query}",
    es: "Búsqueda: {query}",
    fr: "Recherche : {query}",
    de: "Suche: {query}",
    it: "Ricerca: {query}",
    pt: "Pesquisa: {query}",
    zh: "搜索：{query}",
    ja: "検索: {query}",
    ko: "검색: {query}",
    ru: "Поиск: {query}",
    ar: "بحث: {query}",
    hi: "खोज: {query}",
  },
  search_completed_reason: {
    en: "Adaptive search completed after {iterations} search rounds",
    es: "La búsqueda adaptativa se completó después de {iterations} rondas de búsqueda",
    fr: "La recherche adaptative s'est terminée après {iterations} cycles de recherche",
    de: "Die adaptive Suche wurde nach {iterations} Suchrunden abgeschlossen",
    it: "La ricerca adattiva è stata completata dopo {iterations} cicli di ricerca",
    pt: "A busca adaptativa foi concluída após {iterations} rodadas de busca",
    zh: "自适应搜索已在 {iterations} 轮搜索后完成",
    ja: "適応型検索は {iterations} 回の検索ラウンドで完了しました",
    ko: "적응형 검색이 {iterations}번의 검색 라운드 후 완료되었습니다",
    ru: "Адаптивный поиск завершен после {iterations} этапов поиска",
    ar: "اكتمل البحث التكيفي بعد {iterations} جولات من البحث",
    hi: "एडेप्टिव खोज {iterations} खोज चरणों के बाद पूरी हुई",
  },
  google_search_error: {
    en: "An error occurred while performing the search. Please try again.",
    es: "Ocurrió un error al realizar la búsqueda. Inténtalo de nuevo.",
    fr: "Une erreur s'est produite pendant la recherche. Veuillez réessayer.",
    de: "Beim Ausführen der Suche ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Si è verificato un errore durante la ricerca. Riprova.",
    pt: "Ocorreu um erro ao realizar a busca. Tente novamente.",
    zh: "执行搜索时出错了。请再试一次。",
    ja: "検索の実行中にエラーが発生しました。もう一度お試しください。",
    ko: "검색을 수행하는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "Во время выполнения поиска произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "حدث خطأ أثناء تنفيذ البحث. يرجى المحاولة مرة أخرى.",
    hi: "खोज करते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
  generated_image_title: {
    en: "Generated Image",
    es: "Imagen generada",
    fr: "Image générée",
    de: "Generiertes Bild",
    it: "Immagine generata",
    pt: "Imagem gerada",
    zh: "生成的图片",
    ja: "生成された画像",
    ko: "생성된 이미지",
    ru: "Сгенерированное изображение",
    ar: "صورة مُولّدة",
    hi: "जनरेट की गई छवि",
  },
  generated_image_description: {
    en: "Image generated from user prompt",
    es: "Imagen generada a partir de la solicitud del usuario",
    fr: "Image générée à partir de la demande de l'utilisateur",
    de: "Bild, das aus der Benutzeranfrage generiert wurde",
    it: "Immagine generata dalla richiesta dell'utente",
    pt: "Imagem gerada a partir da solicitação do usuário",
    zh: "根据用户提示生成的图片",
    ja: "ユーザープロンプトから生成された画像",
    ko: "사용자 프롬프트로 생성된 이미지",
    ru: "Изображение, созданное по запросу пользователя",
    ar: "صورة تم إنشاؤها من طلب المستخدم",
    hi: "उपयोगकर्ता प्रॉम्प्ट से जनरेट की गई छवि",
  },
  generated_images_reason: {
    en: "Generated images using Stability AI Ultra",
    es: "Se generaron imágenes con Stability AI Ultra",
    fr: "Images générées avec Stability AI Ultra",
    de: "Bilder wurden mit Stability AI Ultra generiert",
    it: "Immagini generate con Stability AI Ultra",
    pt: "Imagens geradas com Stability AI Ultra",
    zh: "已使用 Stability AI Ultra 生成图片",
    ja: "Stability AI Ultra を使用して画像を生成しました",
    ko: "Stability AI Ultra를 사용해 이미지를 생성했습니다",
    ru: "Изображения созданы с помощью Stability AI Ultra",
    ar: "تم إنشاء الصور باستخدام Stability AI Ultra",
    hi: "Stability AI Ultra का उपयोग करके छवियाँ जनरेट की गईं",
  },
  generated_images_error: {
    en: "There was an error generating your images. Please try again.",
    es: "Hubo un error al generar tus imágenes. Inténtalo de nuevo.",
    fr: "Une erreur s'est produite lors de la génération de vos images. Veuillez réessayer.",
    de: "Beim Generieren Ihrer Bilder ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
    it: "Si è verificato un errore durante la generazione delle immagini. Riprova.",
    pt: "Ocorreu un erro ao gerar suas imagens. Tente novamente.",
    zh: "生成图片时出错了。请再试一次。",
    ja: "画像の生成中にエラーが発生しました。もう一度お試しください。",
    ko: "이미지를 생성하는 중 오류가 발생했습니다. 다시 시도해 주세요.",
    ru: "При создании изображений произошла ошибка. Пожалуйста, попробуйте еще раз.",
    ar: "حدث خطأ أثناء إنشاء صورك. يرجى المحاولة مرة أخرى.",
    hi: "आपकी छवियाँ जनरेट करते समय एक त्रुटि हुई। कृपया फिर से कोशिश करें।",
  },
};

export function localizeAiMessage(language: string | null | undefined, key: MessageKey, vars: MessageVars = {}): string {
  const locale = normalizeAiOutputLanguage(language);
  return interpolate(MESSAGES[key][locale] || MESSAGES[key].en, vars);
}
